import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, HostListener, Input, NgZone, OnDestroy, OnInit, AfterViewChecked, ViewChild, effect } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DomSanitizer, SafeHtml, SafeResourceUrl } from '@angular/platform-browser';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { LtmsLoaderComponent } from 'src/app/shared/components/ltms-loader/ltms-loader.component';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import * as _ from 'lodash';
import { AppStateService } from 'src/app/shared/services/helpers/app-state.service';
import { SettingsService } from 'src/app/shared/services/settings';
import { ToastService } from 'src/app/shared/services/toast/toast.service';
import { ChannelSetupService, firstValidationMessage } from 'src/app/shared/services/channel-setup/channel-setup.service';
import { createHostedEmailPreviewUrl } from '../../email-preview-url';
import {
  ChannelProviderOption,
  ChannelSetupAttempt,
  ChannelSetupConfig,
  ChannelSetupProviders,
  ChannelSetupTemplate,
  ChannelDltTemplate,
  CatalogTemplate,
  ChannelFilterTiming,
  emptyChannelSetup,
  emptyAttempt,
  emptyChannelTemplates,
  emptyFilterTiming,
  emptyTemplate,
} from 'src/app/shared/services/channel-setup/channel-setup.models';
import { LANGUAGE_OPTIONS, languageShortCode } from '../../channel-languages';

type ChannelKey = 'email' | 'sms' | 'whatsapp';
type ChannelEditorFocus = 'all' | 'provider' | 'messages';
type PreviewDevice = 'mobile' | 'tablet' | 'desktop';
type ConfirmTone = 'danger' | 'ok';

interface NoticePart {
  text: string;
  strong?: boolean;
}

interface HeaderConfirm {
  title: string;
  message: string;
  tone: ConfirmTone;
  onConfirm: () => void;
  extraAction?: {
    label: string;
    onClick: () => void;
  };
}

const REMINDER_DEFAULT_DELAY_MINUTES = 1440;
const MINUTES_PER_DAY = 1440;
const DELAY_MAX_DAYS = 90;
const DELAY_MAX_MINUTES = DELAY_MAX_DAYS * MINUTES_PER_DAY;
const MAX_LIVE_ATTEMPTS = 35;
const MAX_BACKUP_ATTEMPTS = 5;
const SMS_TRIM_LENGTH_MIN = 1;
const SMS_TRIM_LENGTH_MAX = 100;
const FIELD_MAX = {
  smsSender: 12,
  whatsappSender: 15,
  emailFrom: 255,
  emailReply: 254,
  emailSubject: 255,
  smsBody: 1000,
  emailHtml: 200000,
  whatsappBody: 4096,
};
const SMS_UNIQUE_TOKEN_MAX = 12;
const SMS_UNIQUE_TOKEN_SAMPLE = 'a1b2c3d4e5f6';
const EMAIL_PREVIEW_CSP = '<meta http-equiv="Content-Security-Policy" content="default-src \'none\'; img-src * data: https:; style-src \'unsafe-inline\'; font-src *; script-src \'none\'">';
const EMAIL_IN_TEXT = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/;
const PLAIN_EMAIL = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
const GSM_BASIC_CHARS = new Set(
  `@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !"#¤%&'()*+,-./0123456789:;<=>?¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà`.split(''),
);
const GSM_EXTENDED_CHARS = new Set(['^', '{', '}', '\\', '[', '~', ']', '|', '€', '\f']);

function measureSms(
  text: string,
  language = 'english',
): { encoding: '7bit' | 'unicode'; charsUsed: number; parts: number; charsInSms: number } {
  const value = text || '';
  const forceUnicode = String(language || '').toLowerCase() !== 'english';
  let gsm = !forceUnicode;
  let septets = 0;
  if (!forceUnicode) {
    for (const ch of value) {
      if (GSM_EXTENDED_CHARS.has(ch)) {
        septets += 2;
      } else if (GSM_BASIC_CHARS.has(ch)) {
        septets += 1;
      } else {
        gsm = false;
        break;
      }
    }
  }
  if (gsm) {
    const parts = septets === 0 ? 0 : septets <= 160 ? 1 : Math.ceil(septets / 153);
    return {
      encoding: '7bit',
      charsUsed: septets,
      parts,
      charsInSms: parts <= 1 ? 160 : parts * 153,
    };
  }
  const charsUsed = value.length;
  const parts = charsUsed === 0 ? 0 : charsUsed <= 70 ? 1 : Math.ceil(charsUsed / 67);
  return {
    encoding: 'unicode',
    charsUsed,
    parts,
    charsInSms: parts <= 1 ? 70 : parts * 67,
  };
}


@Component({
  selector: 'app-channel-sequence',
  templateUrl: './channel-sequence.component.html',
  styleUrls: ['./channel-sequence.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, LtmsLoaderComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChannelSequenceComponent implements OnInit, AfterViewChecked, OnDestroy {
  @Input() isWriteAllowed = false;
  @Input() setupMode: 'invites' | 'on_demand' = 'invites';
  loading = true;
  saving = false;
  savingService = false;
  savingDelivery = false;
  confirmBox: HeaderConfirm | null = null;
  loadError = '';
  private persistedAttemptCount = 0;
  private persistedChannelsJson = '';
  private persistedAttemptJson: string[] = [];
  private persistedAttempts: ChannelSetupAttempt[] = [];
  private persistedChannels: ChannelSetupConfig['channels'] | null = null;
  private dirtyGen = 0;
  private attemptChangedCacheGen = -1;
  private attemptChangedCache: boolean[] = [];
  private emailHtmlLoadKey = '';
  private emailHtmlLoadToken = 0;
  brandId: string | null = null;
  private projectBrand: any = null;
  newLanguage = '';
  editingLanguage = '';
  private readonly blankLanguageTemplate = emptyTemplate();
  private messageReviewLang: Partial<Record<ChannelKey, string>> = {};
  selectedAttemptIndex = 0;
  uploadingEmail = false;
  loadingEmailHtml = false;
  private emailHtmlSnapshots: Record<string, string> = {};
  private catalogSms: CatalogTemplate[] = [];
  private catalogEmail: CatalogTemplate[] = [];
  private catalogWhatsapp: CatalogTemplate[] = [];
  filterTiming: ChannelFilterTiming = emptyFilterTiming();
  private smsOptionsCacheKey = '';
  private smsOptionsCache: ChannelDltTemplate[] = [];
  private whatsappOptionsCacheKey = '';
  private whatsappOptionsCache: ChannelDltTemplate[] = [];
  messageTab: ChannelKey = 'sms';
  previewOpen = false;
  previewChannel: ChannelKey = 'sms';
  previewDevice: PreviewDevice = 'mobile';
  channelEditor: ChannelKey | null = null;
  channelEditorFocus: ChannelEditorFocus = 'all';
  emailPreviewUrl: SafeResourceUrl | null = null;
  private emailPreviewObjectUrl = '';
  private lastCaret: { field: keyof ChannelSetupTemplate; start: number; end: number } | null = null;

  @ViewChild('smsBodyInput') smsBodyInput?: ElementRef<HTMLTextAreaElement>;
  @ViewChild('emailSubjectInput') emailSubjectInput?: ElementRef<HTMLInputElement>;
  @ViewChild('emailBodyInput') emailBodyInput?: ElementRef<HTMLTextAreaElement>;
  @ViewChild('whatsappBodyInput') whatsappBodyInput?: ElementRef<HTMLTextAreaElement>;
  @ViewChild('attemptList') attemptList?: ElementRef<HTMLElement>;
  @ViewChild('attemptMain') attemptMain?: ElementRef<HTMLElement>;
  private attemptFootFrame = 0;

  config: ChannelSetupConfig = emptyChannelSetup();
  providers: ChannelSetupProviders = {
    sms: { domestic: [], international: [] },
    email: [],
    whatsapp: { domestic: [], international: [] },
  };

  readonly deliveryChannels: ChannelKey[] = ['sms', 'email', 'whatsapp'];

  get orderedDeliveryChannels(): ChannelKey[] {
    const ready = this.deliveryChannels.filter((key) => this.isChannelSelected(key));
    const pending = this.deliveryChannels.filter((key) => !this.isChannelSelected(key));
    return [...ready, ...pending];
  }

  get selectedDeliveryChannels(): ChannelKey[] {
    return this.deliveryChannels.filter((key) => this.isChannelSelected(key));
  }

  readonly mergeFields = [
    { token: '--customerName--', name: 'customerName', label: 'Customer name' },
    { token: '--feedbackUrl--', name: 'feedbackUrl', label: 'Feedback link' },
    { token: '--shopName--', name: 'shopName', label: 'Touchpoint' },
    { token: '--brandName--', name: 'brandName', label: 'Brand' },
    { token: '--unsubscribeUrl--', name: 'unsubscribeUrl', label: 'Unsubscribe' },
  ];

  readonly fieldMax = FIELD_MAX;
  readonly smsTrimLengthMin = SMS_TRIM_LENGTH_MIN;
  readonly smsTrimLengthMax = SMS_TRIM_LENGTH_MAX;

  get delayMinutesMin(): number {
    return 0;
  }

  get delayMaxDays(): number {
    return this.delayMaxDaysFor(this.selectedAttemptIndex);
  }

  get delayMinutesMax(): number {
    return this.delayMinutesMaxFor(this.selectedAttemptIndex);
  }

  get delayMinutesCharMax(): number {
    return String(this.delayMinutesMax).length;
  }

  get isOnDemand(): boolean {
    return this.setupMode === 'on_demand';
  }

  get attemptHeadId(): string {
    return this.isOnDemand ? 'ns-attempt-head-on-demand' : 'ns-attempt-head';
  }

  get attemptFootId(): string {
    return this.isOnDemand ? 'ns-attempt-foot-label-on-demand' : 'ns-attempt-foot-label';
  }

  get absoluteAtMin(): string {
    return this.toDatetimeLocalValue(new Date());
  }

  get absoluteAtMax(): string {
    const max = new Date();
    max.setDate(max.getDate() + this.delayMaxDays);
    return this.toDatetimeLocalValue(max);
  }

  private attemptTouched = false;
  private ignoreFormActivity = false;

  constructor(
    private readonly appStateService: AppStateService,
    private readonly channelSetupService: ChannelSetupService,
    private readonly toastService: ToastService,
    private readonly sanitizer: DomSanitizer,
    private readonly changeDetector: ChangeDetectorRef,
    private readonly ngZone: NgZone,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly host: ElementRef<HTMLElement>,
    private readonly settingsService: SettingsService,
  ) {
    effect(() => {
      if (!this.appStateService.initialized()) {
        return;
      }
      const authDetails: any = this.appStateService.managerAccess();
      const loggedIn = authDetails?.loggedInManager ?? {};
      const brandDetails: any = this.appStateService.brandDetails();
      this.projectBrand = brandDetails || null;
      const rawBrandId = loggedIn.brand_id || brandDetails?._id || '';
      const nextBrandId = rawBrandId ? String(rawBrandId) : '';
      if (nextBrandId && nextBrandId !== this.brandId) {
        this.brandId = nextBrandId;
        this.load();
        this.settingsService.getFeedbackConfiguration(nextBrandId).subscribe({
          next: (response) => {
            const configuration = response?.data?.brand_configuration?.[0] || {};
            const languages = configuration.languages && typeof configuration.languages === 'object'
              ? configuration.languages
              : {};
            const order = Array.isArray(configuration.languages_order) && configuration.languages_order.length
              ? configuration.languages_order
              : Object.keys(languages);
            order.forEach((key: unknown) => {
              const value = String(key || '').trim();
              if (!value || !Object.prototype.hasOwnProperty.call(languages, value)) {
                return;
              }
              if (LANGUAGE_OPTIONS.some((lang) => lang.key === value)) {
                return;
              }
              LANGUAGE_OPTIONS.unshift({
                key: value,
                name: value,
                native_name: value,
                short: languageShortCode(value),
              });
            });
            this.changeDetector.markForCheck();
          },
        });
      }
    });
  }

  ngOnInit(): void {
    this.host.nativeElement.addEventListener('click', this.onUnauthorizedClick, true);
    this.ngZone.runOutsideAngular(() => {
      document.addEventListener('scroll', this.scheduleAttemptFootLabel, true);
      window.addEventListener('resize', this.scheduleAttemptFootLabel);
    });
  }

  @HostListener('input', ['$event'])
  @HostListener('change', ['$event'])
  onFormFieldActivity(event?: Event): void {
    if (this.loading || this.saving || this.ignoreFormActivity) {
      return;
    }
    const el = event?.target as HTMLElement | undefined;
    const tag = el?.tagName;
    if (tag !== 'INPUT' && tag !== 'TEXTAREA' && tag !== 'SELECT') {
      return;
    }
    if (tag === 'SELECT') {
      const name = (el as HTMLSelectElement).name;
      if (name === 'nsSmsTemplate' || name === 'nsWhatsappTemplate' || name === 'nsEmailTemplate') {
        return;
      }
    }
    this.touchAttempt();
  }

  private readonly onUnauthorizedClick = (event: Event): void => {
    if (this.canUpdate) {
      return;
    }
    const target = event.target as HTMLElement | null;
    if (!target?.closest?.('.is-unauthorized')) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    if (!this.conversationReady) {
      this.showToast(
        'INFO',
        'Set up conversation first',
        'Set up the conversation and publish it before you set up channels.',
        15000,
      );
      return;
    }
    if (this.isOverAttemptLimit) {
      this.showToast('INFO', 'Cannot update', this.attemptOverLimitMessage, 15000);
      return;
    }
    this.showToast('ERROR', 'Not authorized', 'Not authorized');
  };

  private touchAttempt(): void {
    this.attemptTouched = true;
    this.bumpDirtyCache();
    this.changeDetector.markForCheck();
  }

  private destroyed = false;
  private attemptMainObserver?: ResizeObserver;
  private readonly langFitCounts = new Map<string, number>();
  private readonly langFitSignatures = new Map<string, string>();
  private readonly pillWidths = new Map<string, number>();

  ngAfterViewChecked(): void {
    this.bindAttemptListHeight();
    this.measureLangRows();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.attemptMainObserver?.disconnect();
    this.host.nativeElement.removeEventListener('click', this.onUnauthorizedClick, true);
    this.revokeEmailPreview();
    document.removeEventListener('scroll', this.scheduleAttemptFootLabel, true);
    window.removeEventListener('resize', this.scheduleAttemptFootLabel);
    if (this.attemptFootFrame) {
      cancelAnimationFrame(this.attemptFootFrame);
      this.attemptFootFrame = 0;
    }
  }

  private readonly scheduleAttemptFootLabel = (): void => {
    if (this.attemptFootFrame) {
      return;
    }
    this.ngZone.runOutsideAngular(() => {
      this.attemptFootFrame = requestAnimationFrame(() => {
        this.attemptFootFrame = 0;
        const head = document.getElementById(this.attemptHeadId);
        const label = document.getElementById(this.attemptFootId);
        if (!head || !label) {
          return;
        }
        const rect = head.getBoundingClientRect();
        const visible = rect.bottom > 8 && rect.top < window.innerHeight;
        label.classList.toggle('is-hidden', visible);
        label.setAttribute('aria-hidden', visible ? 'true' : 'false');
      });
    });
  };

  get conversationReady(): boolean {
    const url = String(this.config?.prerequisite?.customer_url || '').trim();
    const publishedVersion = String(this.projectBrand?.current_configuration?.['100'] ?? '').trim();
    return Boolean(url) && Boolean(publishedVersion);
  }

  get catchUpDays(): number {
    const n = Number(this.config?.date_limit_interval);
    return Number.isFinite(n) && n > 0 ? Math.round(n) : 5;
  }

  get hasDeliveryChannel(): boolean {
    const channel = String(this.config?.notify_channel || '').toLowerCase();
    return Boolean(channel) && channel !== 'none';
  }

  get headerUpdating(): boolean {
    return this.savingService || this.savingDelivery;
  }

  private derivedNotifyChannel(): string {
    const used = new Set<ChannelKey>();
    this.config.attempts.forEach((attempt) => {
      this.deliveryChannels.forEach((key) => {
        if (this.attemptHasChannelReady(attempt, key)) {
          used.add(key);
        }
      });
    });
    const enabled = this.deliveryChannels.filter((key) => used.has(key));
    if (!enabled.length) {
      return 'none';
    }
    if (enabled.length > 1) {
      return 'all';
    }
    return enabled[0] === 'sms' ? 'phone' : enabled[0];
  }

  get serviceState(): 'paused' | 'live-none' | 'live' {
    if (!this.config.notifications_enabled) {
      return 'paused';
    }
    return this.hasDeliveryChannel ? 'live' : 'live-none';
  }

  get deliveryLabels(): string[] {
    if (!this.hasDeliveryChannel) {
      return [];
    }
    return this.labelsFromCommunicationChannels(
      this.config.communication_channels,
      this.config.notify_channel,
    );
  }

  get backupDeliveryLabels(): string[] {
    const used = new Set<string>();
    this.config.attempts.forEach((attempt) => {
      (attempt.channels || []).forEach((channel) => used.add(String(channel).toLowerCase()));
    });
    return this.deliveryChannels
      .filter((key) => used.has(key))
      .map((key) => this.channelLabel(key));
  }

  get deliverySummary(): string {
    return this.deliveryLabels.length ? this.deliveryLabels.join(', ') : 'None';
  }

  private labelsFromCommunicationChannels(list: string[] | undefined, notifyChannel: string): string[] {
    const set = new Set((list || []).map((item) => String(item).toLowerCase()));
    if (!set.size) {
      const notify = String(notifyChannel || '').toLowerCase();
      if (notify === 'phone') {
        set.add('phone');
      } else if (notify === 'email') {
        set.add('email');
      } else if (notify === 'whatsapp') {
        set.add('whatsapp');
      } else if (notify === 'all') {
        set.add('phone');
        set.add('email');
        set.add('whatsapp');
      }
    }
    const labels: string[] = [];
    if (set.has('phone') || set.has('sms')) {
      labels.push('SMS');
    }
    if (set.has('email')) {
      labels.push('Email');
    }
    if (set.has('whatsapp')) {
      labels.push('WhatsApp');
    }
    return labels;
  }

  private derivedCommunicationChannels(): string[] {
    const channels: string[] = [];
    if (this.config.attempts.some((attempt) => this.attemptHasChannelReady(attempt, 'sms'))) {
      channels.push('phone');
    }
    if (this.config.attempts.some((attempt) => this.attemptHasChannelReady(attempt, 'email'))) {
      channels.push('email');
    }
    if (this.config.attempts.some((attempt) => this.attemptHasChannelReady(attempt, 'whatsapp'))) {
      channels.push('whatsapp');
    }
    return channels;
  }

  setServiceLive(enabled: boolean): void {
    if (this.isOnDemand || !this.brandId || !this.canUpdate || this.headerUpdating || this.loading) {
      return;
    }
    if (this.config.notifications_enabled === enabled) {
      return;
    }
    const title = enabled ? 'Set notification service live' : 'Pause notification service';
    const content = enabled
      ? `Invitation requests will start processing. The last ${this.catchUpDays} days of requests will be picked up. Continue?`
      : 'No invitation requests will be processed until the service is Live again. Continue?';
    this.openConfirm({
      title,
      message: content,
      tone: enabled ? 'ok' : 'danger',
      onConfirm: () => this.persistServiceLive(enabled),
    });
  }

  private openConfirm(box: HeaderConfirm): void {
    this.confirmBox = box;
  }

  private showToast(type: string, header: string, detail: string, duration = 15000): void {
    this.toastService.showPopup(type, header, detail, duration);
    this.changeDetector.markForCheck();
  }

  closeConfirm(proceed: boolean): void {
    const box = this.confirmBox;
    this.confirmBox = null;
    if (proceed && box) {
      box.onConfirm();
    }
  }

  closeConfirmExtra(): void {
    const box = this.confirmBox;
    this.confirmBox = null;
    box?.extraAction?.onClick();
  }

  private persistServiceLive(enabled: boolean): void {
    if (!this.brandId) {
      return;
    }
    this.savingService = true;
    this.channelSetupService.saveNotificationsEnabled(this.brandId, enabled).subscribe({
      next: (saved) => {
        this.applyHeaderSaved(saved);
        this.savingService = false;
        this.showToast(
          'SUCCESS',
          enabled ? 'Live' : 'Paused',
          enabled
            ? `Notification service is live. The last ${this.catchUpDays} days of requests will be processed.`
            : 'Notification service is paused. No records will be processed.',
          15000,
        );
      },
      error: (error) => {
        this.savingService = false;
        this.showToast(
          'ERROR',
          'Could not update service',
          firstValidationMessage(error) || 'Failed to update notification service',
          15000,
        );
      },
    });
  }

  setDeliveryEnabled(enabled: boolean): void {
    if (this.isOnDemand || !this.brandId || !this.canUpdate || this.headerUpdating || this.loading) {
      return;
    }
    if (this.hasDeliveryChannel === enabled) {
      return;
    }
    const notifyChannel = enabled ? this.derivedNotifyChannel() : 'none';
    if (enabled && notifyChannel === 'none') {
      this.showToast(
        'INFO',
        'No delivery channel',
        'Set up SMS, Email, or WhatsApp on an attempt before turning delivery on.',
        15000,
      );
      return;
    }
    const labels = this.labelsFromCommunicationChannels(
      enabled ? this.derivedCommunicationChannels() : [],
      notifyChannel,
    );
    const title = enabled ? 'Turn delivery on' : 'Turn delivery off';
    const content = enabled
      ? `Customers will be contacted on ${labels.join(', ') || 'the configured channels'}. Continue?`
      : 'Records can still process, but customers will not be contacted. Continue?';
    this.openConfirm({
      title,
      message: content,
      tone: enabled ? 'ok' : 'danger',
      onConfirm: () => this.persistDeliveryEnabled(enabled, notifyChannel),
    });
  }

  private persistDeliveryEnabled(enabled: boolean, notifyChannel: string): void {
    if (!this.brandId) {
      return;
    }
    this.savingDelivery = true;
    this.channelSetupService.saveNotifyChannel(
      this.brandId,
      notifyChannel,
      enabled ? this.derivedCommunicationChannels() : undefined,
    ).subscribe({
      next: (saved) => {
        this.applyHeaderSaved(saved);
        this.savingDelivery = false;
        this.showToast(
          'SUCCESS',
          enabled ? 'Delivery on' : 'Delivery none',
          enabled
            ? `Customers will be contacted on ${this.deliverySummary}.`
            : 'Records can still process, but customers will not be contacted.',
          15000,
        );
      },
      error: (error) => {
        this.savingDelivery = false;
        this.showToast(
          'ERROR',
          'Could not update delivery',
          firstValidationMessage(error) || 'Failed to update delivery',
          15000,
        );
      },
    });
  }

  private applyHeaderSaved(saved: ChannelSetupConfig): void {
    if (!saved) {
      return;
    }
    if (saved.notifications_enabled !== undefined) {
      this.config.notifications_enabled = Boolean(saved.notifications_enabled);
    }
    if (saved.notify_channel) {
      this.config.notify_channel = String(saved.notify_channel).toLowerCase();
    }
    if (Array.isArray(saved.communication_channels)) {
      this.config.communication_channels = saved.communication_channels.map((item) => String(item).toLowerCase());
    }
    if (saved.date_limit_interval != null) {
      this.config.date_limit_interval = Number(saved.date_limit_interval) || this.catchUpDays;
    }
  }

  get enabledChannels(): ChannelKey[] {
    const keys = new Set<ChannelKey>();
    this.config.attempts.forEach((attempt) => {
      (attempt.channels || []).forEach((channel) => {
        if (channel === 'email' || channel === 'sms' || channel === 'whatsapp') {
          keys.add(channel);
        }
      });
    });
    if (this.config.channels.email.enabled) {
      keys.add('email');
    }
    if (this.config.channels.sms.enabled) {
      keys.add('sms');
    }
    if (this.config.channels.whatsapp.enabled) {
      keys.add('whatsapp');
    }
    return ['sms', 'email', 'whatsapp'].filter((key) => keys.has(key as ChannelKey)) as ChannelKey[];
  }

  get selectedAttempt(): ChannelSetupAttempt | undefined {
    if (!this.config.attempts.length) {
      return undefined;
    }
    if (!this.config.attempts[this.selectedAttemptIndex]) {
      this.selectedAttemptIndex = 0;
    }
    return this.config.attempts[this.selectedAttemptIndex];
  }

  get activeTemplateChannel(): ChannelKey {
    return this.channelEditor || this.messageTab;
  }

  get currentLanguage(): string {
    const langs = this.languages;
    if (this.editingLanguage && langs.includes(this.editingLanguage)) {
      return this.editingLanguage;
    }
    return this.defaultLanguageFor(this.activeTemplateChannel) || langs[0] || '';
  }

  get currentTemplate(): ChannelSetupTemplate {
    const lang = this.currentLanguage;
    if (!lang) {
      return this.blankLanguageTemplate;
    }
    const bag = this.channelTemplateBag();
    if (!bag[lang]) {
      bag[lang] = emptyTemplate();
    }
    return bag[lang];
  }

  get smsDltOptions(): ChannelDltTemplate[] {
    if (this.isOnDemand) {
      const lang = this.currentLanguage;
      const key = `${lang}|${this.catalogSms.map((item) => this.catalogPick(item, 'id', 'template_id', 'templateId')).join(',')}`;
      if (key !== this.smsOptionsCacheKey) {
        this.smsOptionsCacheKey = key;
        this.smsOptionsCache = this.catalogAsDltOptions(this.catalogSms);
      }
      return this.smsOptionsCache;
    }
    const lang = this.currentLanguage;
    const all = this.config.dlt_templates || [];
    const filtered = all.filter((item) => this.catalogLanguageMatches(item.language, lang)).map((item) => ({
      ...item,
      id: String(item.id || ''),
      rowId: String(item.id || ''),
    }));
    const currentId = String(this.currentTemplate.content_template_id || '').trim();
    if (currentId && !filtered.some((item) => item.id === currentId || item.rowId === currentId)) {
      return [
        {
          id: currentId,
          rowId: currentId,
          name: 'selected-template',
          text: this.currentTemplate.content_template_text || this.currentTemplate.sms_body || '',
          language: lang,
        },
        ...filtered,
      ];
    }
    return filtered;
  }

  get smsTemplateSelectId(): string {
    const stored = String(this.currentTemplate.content_template_id || '').trim();
    if (!stored) {
      return '';
    }
    const hit = this.smsDltOptions.find((item) => item.rowId === stored || item.id === stored);
    return hit?.rowId || hit?.id || stored;
  }

  readonly compareSelectIds = (left: string, right: string): boolean =>
    String(left || '') === String(right || '');

  trackByDltOption(_index: number, option: ChannelDltTemplate): string {
    return option.rowId || option.id;
  }

  trackByCatalogOption(_index: number, option: CatalogTemplate): string {
    return option.id || option.template_id;
  }

  onSmsDltTemplateChange(id: string): void {
    const tpl = this.currentTemplate;
    const nextId = String(id || '').trim();
    const match = this.smsDltOptions.find((item) => (item.rowId || item.id) === nextId || item.id === nextId);
    tpl.content_template_id = match?.id || nextId;
    tpl.content_template_text = match?.text || '';
    tpl.sms_body = match?.text || nextId;
    this.touchAttempt();
  }

  get whatsappTemplateOptions(): ChannelDltTemplate[] {
    if (this.isOnDemand) {
      const lang = this.currentLanguage;
      const key = `${lang}|${this.catalogWhatsapp.map((item) => this.catalogPick(item, 'id', 'template_id', 'templateId')).join(',')}`;
      if (key !== this.whatsappOptionsCacheKey) {
        this.whatsappOptionsCacheKey = key;
        this.whatsappOptionsCache = this.catalogAsDltOptions(this.catalogWhatsapp);
      }
      return this.whatsappOptionsCache;
    }
    const lang = this.currentLanguage;
    const all = this.config.whatsapp_templates || [];
    const filtered = all.filter((item) => this.catalogLanguageMatches(item.language, lang)).map((item) => ({
      ...item,
      id: String(item.id || ''),
      rowId: String(item.id || ''),
    }));
    const currentId = String(this.currentTemplate.whatsapp_template_id || '').trim();
    if (currentId && !filtered.some((item) => item.id === currentId || item.rowId === currentId)) {
      return [
        {
          id: currentId,
          rowId: currentId,
          name: this.currentTemplate.whatsapp_template_name || 'selected-template',
          text: this.currentTemplate.whatsapp_body || '',
          language: lang,
        },
        ...filtered,
      ];
    }
    return filtered;
  }

  get whatsappTemplateSelectId(): string {
    const stored = String(this.currentTemplate.whatsapp_template_id || '').trim();
    if (!stored) {
      return '';
    }
    const hit = this.whatsappTemplateOptions.find((item) => item.rowId === stored || item.id === stored);
    return hit?.rowId || hit?.id || stored;
  }

  onWhatsappTemplateChange(id: string): void {
    const tpl = this.currentTemplate;
    const nextId = String(id || '').trim();
    const match = this.whatsappTemplateOptions.find((item) => (item.rowId || item.id) === nextId || item.id === nextId);
    tpl.whatsapp_template_id = match?.id || nextId;
    tpl.whatsapp_template_name = match?.name || '';
    tpl.whatsapp_body = match?.text || '';
    this.touchAttempt();
  }

  get emailCatalogOptions(): CatalogTemplate[] {
    const lang = this.currentLanguage;
    return this.catalogEmail.filter((item) => this.catalogLanguageMatches(item.template_language, lang));
  }

  onEmailCatalogChange(id: string): void {
    const tpl = this.currentTemplate;
    const nextId = id || '';
    if ((tpl.email_template_id || '') === nextId) {
      return;
    }
    const match = this.emailCatalogOptions.find((item) => item.id === nextId);
    tpl.email_template_id = nextId;
    tpl.template_name = match?.template_title || match?.template_name || '';
    tpl.email_subject = match?.email_subject || match?.template_name || '';
    tpl.email_body = '';
    tpl.template_file_id = match?.template_id || '';
    tpl.template_file_name = match?.template_name || '';
    this.emailHtmlLoadToken += 1;
    this.touchAttempt();
    this.maybeLoadEmailPreview();
  }

  private catalogAsDltOptions(list: CatalogTemplate[]): ChannelDltTemplate[] {
    const lang = this.currentLanguage;
    const mapped = list.map((item) => {
      const rowId = this.catalogPick(item, 'id', '_id');
      const templateId = this.catalogPick(item, 'template_id', 'templateId');
      return {
        id: templateId || rowId,
        rowId: rowId || templateId,
        name: this.catalogPick(item, 'template_title', 'templateTitle') || this.catalogPick(item, 'template_name', 'templateName'),
        text: this.catalogPick(item, 'template_text', 'templateText'),
        language: this.catalogPick(item, 'template_language', 'templateLanguage'),
      };
    }).filter((item) => item.rowId || item.id);
    const matching = mapped.filter((item) => this.catalogLanguageMatches(item.language, lang));
    return matching;
  }

  private catalogPick(item: CatalogTemplate | Record<string, unknown>, ...keys: string[]): string {
    const rec = item as unknown as Record<string, unknown>;
    for (const key of keys) {
      const value = rec[key];
      if (value != null && String(value).trim()) {
        return String(value).trim();
      }
    }
    return '';
  }

  hasTemplatesForLanguage(channel: ChannelKey): boolean {
    const lang = this.currentLanguage;
    if (!lang) {
      return false;
    }
    if (channel === 'email') {
      return this.emailCatalogOptions.length > 0;
    }
    if (channel === 'sms') {
      if (this.isOnDemand) {
        return this.smsDltOptions.length > 0;
      }
      return (this.config.dlt_templates || []).some((item) => this.catalogLanguageMatches(item.language, lang));
    }
    if (this.isOnDemand) {
      return this.whatsappTemplateOptions.length > 0;
    }
    return (this.config.whatsapp_templates || []).some((item) => this.catalogLanguageMatches(item.language, lang));
  }

  private catalogLanguageMatches(templateLanguage: string | undefined, lang: string): boolean {
    const left = this.normalizeLanguageKey(templateLanguage);
    const right = this.normalizeLanguageKey(lang);
    if (!left || !right) {
      return false;
    }
    if (left === right) {
      return true;
    }
    const option = LANGUAGE_OPTIONS.find((item) => this.normalizeLanguageKey(item.key) === right);
    if (!option) {
      return false;
    }
    if (left === this.normalizeLanguageKey(option.name)) {
      return true;
    }
    const short = this.normalizeLanguageKey(option.short);
    return left.length <= 3 && left === short;
  }

  private normalizeLanguageKey(value: string | undefined): string {
    return String(value || '').trim().toLowerCase().replace(/[\s_-]+/g, '');
  }

  get languages(): string[] {
    const keys = Object.keys(this.channelTemplateBag());
    return this.orderLanguages(keys, this.activeTemplateChannel);
  }

  private channelTemplateBag(): Record<string, ChannelSetupTemplate> {
    const attempt = this.selectedAttempt;
    if (!attempt) {
      return {};
    }
    if (!attempt.templates) {
      attempt.templates = emptyChannelTemplates();
    }
    const key = this.activeTemplateChannel;
    if (!attempt.templates[key]) {
      attempt.templates[key] = {};
    }
    return attempt.templates[key];
  }

  get availableLanguages(): Array<{ key: string; name: string; native_name: string; short: string }> {
    const used = new Set(this.languages);
    return LANGUAGE_OPTIONS.filter((option) => !used.has(option.key));
  }

  get attemptMessageTabs(): ChannelKey[] {
    return this.selectedDeliveryChannels;
  }

  languagesFor(channel: ChannelKey): string[] {
    return this.languagesForAttempt(this.selectedAttempt, channel);
  }

  languagesForAttempt(attempt: ChannelSetupAttempt | undefined, channel: ChannelKey): string[] {
    const keys = Object.keys(attempt?.templates?.[channel] || {});
    return this.orderLanguages(keys, channel, attempt);
  }

  channelLanguageCount(attempt: ChannelSetupAttempt | undefined, channel: ChannelKey): number {
    return this.languagesForAttempt(attempt, channel).length;
  }

  languageCountLabel(count: number): string {
    return count === 1 ? '1 language' : `${count} languages`;
  }

  languageShort(key: string): string {
    const match = LANGUAGE_OPTIONS.find((option) => option.key === key);
    return match?.short || languageShortCode(key, this.languageLabel(key));
  }

  channelLangShorts(channel: ChannelKey, attempt: ChannelSetupAttempt | undefined = this.selectedAttempt): Array<{ code: string; title: string; isDefault: boolean }> {
    const fallback = this.defaultLanguageFor(channel, attempt);
    return this.languagesForAttempt(attempt, channel).map((lang) => ({
      code: this.languageShort(lang),
      title: this.languageLabel(lang),
      isDefault: lang === fallback,
    }));
  }

  channelAttemptLangHint(key: ChannelKey): string {
    if (!this.isChannelSelected(key)) {
      return 'Not on this attempt';
    }
    const shorts = this.channelLangShorts(key).map((item) => item.code).join(' ');
    return shorts ? `On this attempt · ${shorts}` : 'On this attempt';
  }

  attemptChannelLangChips(attempt: ChannelSetupAttempt): Array<{
    key: ChannelKey;
    name: string;
    shorts: Array<{ code: string; title: string; isDefault: boolean }>;
    title: string;
  }> {
    return (attempt.channels || []).map((channel) => {
      const key = channel as ChannelKey;
      const shorts = this.channelLangShorts(key, attempt);
      return {
        key,
        name: this.channelLabel(key),
        shorts,
        title: shorts.map((item) => (
          item.isDefault ? `${item.title} is the default language` : item.title
        )).join(', ') || this.channelLabel(key),
      };
    });
  }

  fittedLangs(attemptIndex: number, chip: { key: ChannelKey; shorts: Array<{ code: string; title: string; isDefault: boolean }> }): Array<{ code: string; title: string; isDefault: boolean }> {
    const fit = this.langFitCounts.get(this.langRowKey(attemptIndex, chip.key));
    if (fit == null || fit >= chip.shorts.length) {
      return chip.shorts;
    }
    return chip.shorts.slice(0, fit);
  }

  fittedExtra(attemptIndex: number, chip: { key: ChannelKey; shorts: Array<{ code: string; title: string; isDefault: boolean }> }): number {
    return chip.shorts.length - this.fittedLangs(attemptIndex, chip).length;
  }

  fittedExtraTitle(attemptIndex: number, chip: { key: ChannelKey; shorts: Array<{ code: string; title: string; isDefault: boolean }> }): string {
    return chip.shorts.slice(this.fittedLangs(attemptIndex, chip).length).map((item) => item.title).join(', ');
  }

  private orderLanguages(keys: string[], channel: ChannelKey, attempt = this.selectedAttempt): string[] {
    const list = [...keys];
    const def = this.defaultLanguageFor(channel, attempt);
    if (!def || !list.includes(def)) {
      return list;
    }
    return [def, ...list.filter((lang) => lang !== def)];
  }

  reviewLanguage(channel: ChannelKey): string {
    const langs = this.languagesFor(channel);
    const chosen = this.messageReviewLang[channel];
    if (chosen && langs.includes(chosen)) {
      return chosen;
    }
    return this.defaultLanguageFor(channel);
  }

  setReviewLanguage(channel: ChannelKey, lang: string): void {
    this.messageReviewLang[channel] = lang;
  }

  templateFor(channel: ChannelKey, lang = this.reviewLanguage(channel)): ChannelSetupTemplate {
    return this.selectedAttempt?.templates?.[channel]?.[lang] || emptyTemplate();
  }

  hasEmailTemplateFor(tpl: ChannelSetupTemplate): boolean {
    return Boolean(
      tpl?.email_body?.trim()
      || String(tpl?.email_template_id || '').trim()
      || String(tpl?.template_file_id || '').trim()
      || tpl?.template_name?.trim(),
    );
  }

  private inviteEmailReady(tpl: ChannelSetupTemplate | undefined): boolean {
    if (!tpl?.email_subject?.trim()) {
      return false;
    }
    return this.hasEmailTemplateFor(tpl);
  }

  emailHtmlLabelFor(tpl: ChannelSetupTemplate): string {
    if (tpl?.template_name?.trim()) {
      return tpl.template_name;
    }
    if (tpl?.template_file_name?.trim()) {
      return tpl.template_file_name;
    }
    if (this.hasEmailTemplateFor(tpl)) {
      return 'Template saved';
    }
    return '';
  }

  smsTemplateText(tpl: ChannelSetupTemplate | undefined): string {
    const text = String(tpl?.content_template_text || '').trim();
    if (text) {
      return text;
    }
    const body = String(tpl?.sms_body || '').trim();
    const id = String(tpl?.content_template_id || '').trim();
    if (body && body !== id) {
      return body;
    }
    return '';
  }

  smsReviewStats(channel: ChannelKey): {
    encodingLabel: string;
    parts: number;
    charsUsed: number;
    charsInSms: number;
    over: boolean;
  } | null {
    const lang = this.reviewLanguage(channel);
    const body = String(this.smsTemplateText(this.templateFor(channel, lang)) || '').trim();
    if (!body) {
      return null;
    }
    const measure = measureSms(this.applyMergeTokens(body), lang);
    return {
      encodingLabel: measure.encoding === 'unicode' ? 'unicode (70/67)' : '7bit (160/153)',
      parts: measure.parts,
      charsUsed: measure.charsUsed,
      charsInSms: measure.charsInSms,
      over: measure.parts > 1,
    };
  }

  reviewEmailTemplate(channel: ChannelKey, action: 'view' | 'preview' | 'download'): void {
    this.messageTab = channel;
    this.editingLanguage = this.reviewLanguage(channel);
    if (action === 'preview') {
      this.previewEmailTemplate();
      return;
    }
    if (action === 'view') {
      this.viewEmailTemplate();
      return;
    }
    this.downloadEmailTemplate();
  }

  get previewChannelTabs(): ChannelKey[] {
    if (this.channelEditor) {
      return [this.channelEditor];
    }
    if (this.previewChannel && this.attemptMessageTabs.includes(this.previewChannel)) {
      return [this.previewChannel];
    }
    return this.attemptMessageTabs.length ? this.attemptMessageTabs : [];
  }

  channelStatus(key: ChannelKey): 'on' | 'off' | 'incomplete' {
    if (!this.config.channels[key].enabled) {
      return 'off';
    }
    if (this.isChannelComplete(key)) {
      return 'on';
    }
    return 'incomplete';
  }

  isChannelComplete(key: ChannelKey, channels = this.config.channels): boolean {
    const ch = channels;
    if (key === 'email') {
      const from = String(ch.email.from || '').trim();
      const replyTo = String(ch.email.reply_to || '').trim();
      return Boolean(ch.email.provider && from && EMAIL_IN_TEXT.test(from) && (!replyTo || PLAIN_EMAIL.test(replyTo)));
    }
    if (key === 'sms') {
      return Boolean(ch.sms.sender_id && ch.sms.provider && ch.sms.international_provider);
    }
    return Boolean(ch.whatsapp.sender_id && ch.whatsapp.domestic_provider && ch.whatsapp.international_provider);
  }

  isChannelSelected(key: ChannelKey): boolean {
    if (!this.selectedAttempt?.channels.includes(key)) {
      return false;
    }
    if (this.isOnDemand) {
      return this.isChannelComplete(key);
    }
    return this.isAttemptChannelConfigured(key);
  }

  get selectedAttemptChannelCount(): number {
    return this.deliveryChannels.filter((key) => this.isChannelSelected(key)).length;
  }

  canRemoveAttemptChannel(key: ChannelKey): boolean {
    return Boolean(this.canUpdate && this.isChannelSelected(key) && this.selectedAttemptChannelCount > 1);
  }

  removeAttemptChannelHint(key: ChannelKey): string {
    if (!this.isChannelSelected(key)) {
      return '';
    }
    if (this.selectedAttemptChannelCount <= 1) {
      return 'Keep at least one channel on this attempt';
    }
    return `Remove ${this.channelLabel(key)} from this attempt`;
  }

  removeAttemptChannel(key: ChannelKey): void {
    if (!this.selectedAttempt || !this.canUpdate) {
      return;
    }
    if (!this.isChannelSelected(key)) {
      return;
    }
    if (this.selectedAttemptChannelCount <= 1) {
      this.showToast(
        'INFO',
        'Keep one channel',
        'This attempt needs at least one delivery channel.',
        15000,
      );
      return;
    }
    this.selectedAttempt.channels = this.selectedAttempt.channels.filter((channel) => channel !== key);
    if (!this.selectedAttempt.templates) {
      this.selectedAttempt.templates = emptyChannelTemplates();
    }
    this.selectedAttempt.templates[key] = {};
    if (this.channelEditor === key) {
      this.channelEditor = null;
    }
    this.syncProjectChannelsFromAttempts();
    this.syncMessageTab();
    this.selectDefaultLanguageForActiveChannel();
    this.touchAttempt();
  }

  isAttemptChannelConfigured(key: ChannelKey): boolean {
    return this.attemptHasChannelReady(this.selectedAttempt, key);
  }

  private attemptHasChannelReady(
    attempt: ChannelSetupAttempt | undefined,
    key: ChannelKey,
    channels = this.config.channels,
  ): boolean {
    if (!attempt || !this.isChannelComplete(key, channels)) {
      return false;
    }
    const tplBag = attempt.templates?.[key] || {};
    return Object.values(tplBag).some((tpl) => {
      if (key === 'sms') {
        return this.isOnDemand
          ? Boolean(tpl.content_template_id?.trim())
          : Boolean(tpl.sms_body?.trim());
      }
      if (key === 'email') {
        return this.isOnDemand
          ? Boolean(tpl.email_template_id?.trim() || tpl.template_name?.trim() || tpl.template_file_id?.trim())
          : this.inviteEmailReady(tpl);
      }
      return Boolean(tpl.whatsapp_body?.trim() || tpl.whatsapp_template_id?.trim());
    });
  }

  channelSetupGap(key: ChannelKey): string {
    if (key === 'sms') {
      if (!this.config.channels.sms.sender_id?.trim()) {
        return 'Add an SMS sender ID.';
      }
      if (!this.config.channels.sms.provider || !this.config.channels.sms.international_provider) {
        return 'Select India and international SMS providers.';
      }
      if (!this.currentLanguage) {
        return 'Choose a language, then add the template details.';
      }
      if (!this.currentTemplate.sms_body?.trim() && !this.currentTemplate.content_template_id?.trim()) {
        return this.isOnDemand ? 'Select an SMS template.' : 'Write the SMS message.';
      }
      if (this.config.channels.sms.sms_trim_enabled && !this.smsTrimLengthValid) {
        return 'Enter a recipient name max length between 1 and 100.';
      }
      if (this.config.channels.sms.sms_trim_enabled && !this.smsTrimNameValid) {
        return 'Enter a default recipient name no longer than the max length.';
      }
    }
    if (key === 'email') {
      if (!this.config.channels.email.provider) {
        return 'Select an email provider.';
      }
      if (!this.config.channels.email.from?.trim()) {
        return 'Add a from address.';
      }
      if (!this.emailFromLooksValid) {
        return 'Use From as Name <email@domain.com>, for example LitmusWorld <feedback@litmusworld.net>.';
      }
      if (this.config.channels.email.reply_to && !this.replyToLooksValid) {
        return 'Enter a valid reply-to email address.';
      }
      if (!this.currentLanguage) {
        return 'Choose a language, then add the template details.';
      }
      if (this.isOnDemand) {
        if (
          !this.currentTemplate.email_template_id?.trim()
          && !this.currentTemplate.template_name?.trim()
          && !this.currentTemplate.template_file_id?.trim()
        ) {
          return 'Select or upload an email template.';
        }
      } else {
        if (!this.currentTemplate.email_subject?.trim()) {
          return 'Add an email subject.';
        }
        if (!this.inviteEmailReady(this.currentTemplate)) {
          return 'Upload the email HTML.';
        }
      }
    }
    if (key === 'whatsapp') {
      if (!this.config.channels.whatsapp.sender_id?.trim()) {
        return 'Add a WhatsApp sender ID.';
      }
      if (!this.config.channels.whatsapp.domestic_provider) {
        return 'Select a WhatsApp provider for India.';
      }
      if (!this.config.channels.whatsapp.international_provider) {
        return 'Select an international WhatsApp provider.';
      }
      if (!this.currentLanguage) {
        return 'Choose a language, then add the template details.';
      }
      if (!this.currentTemplate.whatsapp_template_id?.trim()) {
        return 'Select a WhatsApp Template Id.';
      }
      if (!this.isOnDemand && !this.currentTemplate.whatsapp_body?.trim()) {
        return 'Select a WhatsApp template that has template text.';
      }
    }
    return 'Fill every required field for this channel.';
  }

  providerUnavailable(option: ChannelProviderOption | undefined): boolean {
    return Boolean(option && option.is_available === false);
  }

  findProvider(list: ChannelProviderOption[], id: string): ChannelProviderOption | undefined {
    return list.find((item) => item.id === id);
  }

  providerName(list: ChannelProviderOption[], id: string): string {
    const value = String(id || '').trim();
    if (!value) {
      return '—';
    }
    const match = this.findProvider(list, value);
    if (!match) {
      return value;
    }
    return match.is_available === false ? `${match.name} (unavailable)` : match.name;
  }

  displayValue(value: string | number | null | undefined): string {
    const text = String(value ?? '').trim();
    return text || '—';
  }

  get smsTrimSummary(): string {
    if (!this.config.channels.sms.sms_trim_enabled) {
      return 'Off';
    }
    const length = this.displayValue(this.config.channels.sms.recipient_name_length);
    const name = this.displayValue(this.config.channels.sms.default_recipient_name);
    return `On · max ${length} · ${name}`;
  }

  setSmsTrimEnabled(enabled: boolean): void {
    this.config.channels.sms.sms_trim_enabled = Boolean(enabled);
    if (!enabled) {
      return;
    }
    if (!this.config.channels.sms.recipient_name_length) {
      this.config.channels.sms.recipient_name_length = 8;
    }
    if (!this.config.channels.sms.default_recipient_name?.trim()) {
      this.config.channels.sms.default_recipient_name = 'Customer';
    }
    this.onSmsRecipientLengthChange(this.config.channels.sms.recipient_name_length);
  }

  onSmsRecipientLengthChange(value?: number | string): void {
    const next = value === undefined ? this.config.channels.sms.recipient_name_length : value;
    this.config.channels.sms.recipient_name_length = this.clampSmsTrimLength(next);
    const limit = Number(this.config.channels.sms.recipient_name_length) || 0;
    const name = this.config.channels.sms.default_recipient_name || '';
    if (limit > 0 && name.length > limit) {
      this.config.channels.sms.default_recipient_name = name.slice(0, limit);
    }
  }

  setDelayMinutes(value: number | string | null): void {
    if (!this.selectedAttempt) {
      return;
    }
    const digits = String(value ?? '').replace(/\D/g, '').slice(0, this.delayMinutesCharMax);
    this.selectedAttempt.delay_minutes = this.clampDelayMinutes(digits, this.selectedAttemptIndex);
  }

  onDelayMinutesInput(event: Event): void {
    const el = event.target as HTMLInputElement;
    const digits = String(el.value || '').replace(/\D/g, '').slice(0, this.delayMinutesCharMax);
    if (el.value !== digits) {
      el.value = digits;
    }
    this.setDelayMinutes(digits);
  }

  setAbsoluteAt(value: string): void {
    if (!this.selectedAttempt) {
      return;
    }
    this.selectedAttempt.absolute_at = this.clampAbsoluteAt(value, this.selectedAttemptIndex);
  }

  setBoundedText(path: 'smsSender' | 'whatsappSender' | 'emailFrom' | 'emailReply', value: string): void {
    const max = FIELD_MAX[path];
    const next = String(value || '').slice(0, max);
    if (path === 'smsSender') {
      this.config.channels.sms.sender_id = next;
      return;
    }
    if (path === 'whatsappSender') {
      this.config.channels.whatsapp.sender_id = next;
      return;
    }
    if (path === 'emailFrom') {
      this.config.channels.email.from = next;
      return;
    }
    this.config.channels.email.reply_to = next;
  }

  setTemplateText(field: 'sms_body' | 'email_subject' | 'email_body' | 'whatsapp_body', value: string): void {
    const max = field === 'sms_body'
      ? FIELD_MAX.smsBody
      : field === 'email_subject'
        ? FIELD_MAX.emailSubject
        : field === 'email_body'
          ? FIELD_MAX.emailHtml
          : FIELD_MAX.whatsappBody;
    this.currentTemplate[field] = String(value || '').slice(0, max) as never;
  }

  blockNonIntegerKeys(event: KeyboardEvent): void {
    if (['e', 'E', '+', '-', '.'].includes(event.key)) {
      event.preventDefault();
    }
  }

  delayMinutesMaxFor(_index: number): number {
    return DELAY_MAX_MINUTES;
  }

  delayMaxDaysFor(_index: number): number {
    return DELAY_MAX_DAYS;
  }

  delayMinutesGap(attempt: ChannelSetupAttempt | undefined, index = this.selectedAttemptIndex): string {
    if (!attempt) {
      return '';
    }
    if (!this.isOnDemand && attempt.timing === 'absolute') {
      return '';
    }
    const minutes = Number(attempt.delay_minutes);
    const max = this.delayMinutesMaxFor(index);
    if (Number.isFinite(minutes) && minutes >= 0 && minutes <= max) {
      const windowGap = this.delayWindowGapForMinutes(this.attemptSendOffsetMinutes(index));
      return windowGap ? `${windowGap} Filters & timing.` : '';
    }
    return `${this.attemptDisplayName(index)} delay must be between 0 and ${max} minutes (${this.delayMaxDaysFor(index)} days).`;
  }

  get requestProcessDays(): number | null {
    const n = Number(this.filterTiming?.date_limit_interval ?? this.config?.date_limit_interval);
    return Number.isFinite(n) && n > 0 ? n : null;
  }

  get linkExpiryHours(): number | null {
    const n = Number(this.filterTiming?.expiration_delay_hours);
    return Number.isFinite(n) && n >= 0 ? n : null;
  }

  get requestProcessMinutes(): number | null {
    return this.requestProcessDays == null ? null : this.requestProcessDays * MINUTES_PER_DAY;
  }

  get linkExpiryMinutes(): number | null {
    return this.linkExpiryHours == null ? null : this.linkExpiryHours * 60;
  }

  attemptSendOffsetMinutes(index: number): number {
    const attempt = this.config.attempts[index];
    if (!attempt || (!this.isOnDemand && attempt.timing === 'absolute')) {
      return 0;
    }
    const minutes = Math.round(Number(attempt.delay_minutes));
    const delay = Number.isFinite(minutes) && minutes > 0 ? minutes : 0;
    if (this.isOnDemand || index <= 0 || Number(attempt.based_on) === 1) {
      return delay;
    }
    return this.attemptSendOffsetMinutes(index - 1) + delay;
  }

  get latestBackupDelayMinutes(): number {
    return (this.config.attempts || []).reduce((max, _attempt, index) => {
      const minutes = this.attemptSendOffsetMinutes(index);
      return minutes > max ? minutes : max;
    }, 0);
  }

  get attemptsPastSendWindow(): number[] {
    if (this.loading) {
      return [];
    }
    return (this.config.attempts || []).reduce((indexes: number[], _attempt, index) => {
      if (this.attemptDelayMissesWindow(index)) {
        indexes.push(index);
      }
      return indexes;
    }, []);
  }

  attemptDelayMissesWindow(index: number): boolean {
    return Boolean(this.delayWindowGapForMinutes(this.attemptSendOffsetMinutes(index)));
  }

  get sequenceWindowNotice(): NoticePart[] {
    const lateIndexes = this.attemptsPastSendWindow;
    if (!lateIndexes.length) {
      return [];
    }
    const minutes = this.latestBackupDelayMinutes;
    const processMinutes = this.requestProcessMinutes;
    const expiryMinutes = this.linkExpiryMinutes;
    const overProcess = processMinutes != null && minutes > processMinutes;
    const overExpiry = expiryMinutes != null && minutes > expiryMinutes;
    if (!overProcess && !overExpiry) {
      return [];
    }
    const parts: NoticePart[] = [];
    const push = (text: string, strong = false) => parts.push({ text, strong });
    if (lateIndexes.length === 1) {
      push(this.attemptDisplayName(lateIndexes[0]), true);
      push(' is scheduled ');
    } else if (lateIndexes.length === 2) {
      push(this.attemptDisplayName(lateIndexes[0]), true);
      push(' and ');
      push(this.attemptDisplayName(lateIndexes[1]), true);
      push(' are scheduled ');
    } else {
      push(String(lateIndexes.length), true);
      push(' attempts are scheduled ');
    }
    push(this.formatDurationMinutes(minutes), true);
    push(' after the request, but the ');
    if (overProcess && processMinutes != null) {
      push('request processing period', true);
      push(' is only ');
      push(this.formatDurationMinutes(processMinutes), true);
    }
    if (overProcess && overExpiry) {
      push(' and the ');
    }
    if (overExpiry && expiryMinutes != null) {
      push('feedback link', true);
      push(' expires after ');
      push(this.formatDurationMinutes(expiryMinutes), true);
    }
    push(lateIndexes.length === 1 ? '. Shorten this delay, or increase ' : '. Shorten those delays, or increase ');
    if (overProcess) {
      push('Request processing period', true);
    }
    if (overProcess && overExpiry) {
      push(' and ');
    }
    if (overExpiry) {
      push('Link expiration', true);
    }
    push(' in ');
    return parts;
  }

  get sequenceWindowErrors(): string[] {
    const lateIndexes = this.attemptsPastSendWindow;
    if (!lateIndexes.length) {
      return [];
    }
    const subject = lateIndexes.length <= 2
      ? `${this.joinAttemptNames(lateIndexes)} ${lateIndexes.length === 1 ? 'is scheduled' : 'are scheduled'}`
      : `These ${lateIndexes.length} attempts are scheduled`;
    return this.delayWindowMessages(this.latestBackupDelayMinutes, subject, true);
  }

  delayWindowGapForMinutes(minutes: number): string {
    return this.delayWindowMessages(minutes, 'This attempt is scheduled', false).join(' ');
  }

  delayWindowRows(attempt: ChannelSetupAttempt | undefined, index = this.selectedAttemptIndex): string[] {
    if (!attempt || (!this.isOnDemand && attempt.timing === 'absolute')) {
      return [];
    }
    const minutes = Number(attempt.delay_minutes);
    const max = this.delayMinutesMaxFor(index);
    if (!Number.isFinite(minutes) || minutes < 0 || minutes > max) {
      return [];
    }
    return this.delayWindowMessages(this.attemptSendOffsetMinutes(index), 'This attempt is scheduled', false);
  }

  openFiltersAndTiming(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.goToFiltersAndTiming();
  }

  private joinAttemptNames(indexes: number[]): string {
    const names = indexes.map((index) => this.attemptDisplayName(index));
    if (names.length <= 1) {
      return names[0] || '';
    }
    if (names.length === 2) {
      return `${names[0]} and ${names[1]}`;
    }
    return `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`;
  }

  private delayWindowMessages(minutes: number, subject: string, alreadyMissed = false): string[] {
    const messages: string[] = [];
    const processMinutes = this.requestProcessMinutes;
    const expiryMinutes = this.linkExpiryMinutes;
    const sendAfter = this.formatDurationMinutes(minutes);
    const fixHint = alreadyMissed ? 'those delays' : 'this delay';
    if (processMinutes != null && minutes > processMinutes) {
      messages.push(
        `${subject} ${sendAfter} after the request, but request processing period is only ${this.formatDurationMinutes(processMinutes)}. Shorten ${fixHint}, or increase Request processing period in`,
      );
    }
    if (expiryMinutes != null && minutes > expiryMinutes) {
      messages.push(
        `${subject} ${sendAfter} after the request, but the feedback link expires after ${this.formatDurationMinutes(expiryMinutes)}. Shorten ${fixHint}, or increase Link expiration in`,
      );
    }
    return messages;
  }

  private maxOnDemandDelayMinutes(): number {
    const caps = [DELAY_MAX_MINUTES];
    if (this.requestProcessMinutes != null) {
      caps.push(this.requestProcessMinutes);
    }
    if (this.linkExpiryMinutes != null) {
      caps.push(this.linkExpiryMinutes);
    }
    return Math.max(0, Math.min(...caps));
  }

  private clampOnDemandCreateDelay(minutes: number): number {
    const allowed = this.maxOnDemandDelayMinutes();
    const gap = this.delayWindowGapForMinutes(minutes);
    if (gap && minutes > allowed) {
      this.showToast(
        'INFO',
        'Delay adjusted',
        `A 1-day reminder would send too late. Delay was set to ${this.formatDurationMinutes(allowed)}. ${gap}`,
        15000,
      );
      return allowed;
    }
    return minutes;
  }

  private formatDurationMinutes(minutes: number): string {
    const value = Math.max(0, Math.round(Number(minutes) || 0));
    if (value === 0) {
      return '0 minutes';
    }
    const days = Math.floor(value / MINUTES_PER_DAY);
    const hours = Math.floor((value % MINUTES_PER_DAY) / 60);
    const leftover = value % 60;
    const parts: string[] = [];
    if (days) {
      parts.push(days === 1 ? '1 day' : `${days} days`);
    }
    if (hours) {
      parts.push(hours === 1 ? '1 hour' : `${hours} hours`);
    }
    if (leftover) {
      parts.push(leftover === 1 ? '1 minute' : `${leftover} minutes`);
    }
    return parts.join(' ');
  }

  private clampDelayMinutes(value: number | string | null | undefined, index: number): number {
    const minutes = Math.round(Number(value));
    if (!Number.isFinite(minutes) || minutes < 0) {
      return 0;
    }
    return Math.min(this.delayMinutesMaxFor(index), minutes);
  }

  private clampSmsTrimLength(value: number | string | null | undefined): number {
    const length = Math.round(Number(value));
    if (!Number.isFinite(length) || length < SMS_TRIM_LENGTH_MIN) {
      return SMS_TRIM_LENGTH_MIN;
    }
    return Math.min(SMS_TRIM_LENGTH_MAX, length);
  }

  private clampAbsoluteAt(value: string, index: number): string {
    const raw = String(value || '').trim();
    if (!raw) {
      return '';
    }
    const parsed = new Date(raw);
    if (Number.isNaN(parsed.getTime())) {
      return '';
    }
    const min = new Date();
    const max = new Date();
    max.setDate(max.getDate() + this.delayMaxDaysFor(index));
    const clamped = parsed < min ? min : (parsed > max ? max : parsed);
    return this.toDatetimeLocalValue(clamped);
  }

  private toDatetimeLocalValue(date: Date): string {
    const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
    return local.toISOString().slice(0, 16);
  }

  get smsTrimLengthValid(): boolean {
    const length = Number(this.config.channels.sms.recipient_name_length);
    return Number.isFinite(length) && length >= 1 && length <= 100;
  }

  get smsTrimNameValid(): boolean {
    const name = (this.config.channels.sms.default_recipient_name || '').trim();
    const length = Number(this.config.channels.sms.recipient_name_length) || 0;
    return Boolean(name) && name.length <= length;
  }

  load(): void {
    if (!this.brandId) {
      return;
    }
    this.loading = true;
    this.loadError = '';
    const brandId = this.brandId;
    forkJoin({
      config: this.isOnDemand
        ? this.channelSetupService.getOnDemand(brandId)
        : this.channelSetupService.getConfig(brandId),
      providers: this.channelSetupService.getProviders(brandId),
      filterTiming: this.channelSetupService.getFilterTiming(brandId).pipe(
        catchError(() => of({
          ...emptyFilterTiming(),
          date_limit_interval: null,
          expiration_delay_hours: null,
        })),
      ),
    }).subscribe({
      next: ({ config, providers, filterTiming }) => {
        // Let the route paint the loader before building the attempt form.
        // Doing both in the navigation turn freezes the tab; a refresh does not.
        setTimeout(() => {
          if (!this.destroyed) {
            this.receiveSetup(config, providers, filterTiming);
          }
        });
      },
      error: (error) => {
        this.loading = false;
        this.loadError = error?.error?.message || error?.message || 'Could not load notification setup';
        this.changeDetector.markForCheck();
      },
    });
  }

  private receiveSetup(
    config: ChannelSetupConfig,
    providers: ChannelSetupProviders,
    filterTiming: ChannelFilterTiming,
  ): void {
    const embedded = config.template_catalogs;
    if (this.isOnDemand && !embedded) {
      this.loadOnDemandCatalogs(this.brandId || '', config, providers, filterTiming);
      return;
    }
    this.applyLoadedSetup(config, providers, filterTiming, embedded);
  }

  private loadOnDemandCatalogs(
    brandId: string,
    config: ChannelSetupConfig,
    providers: ChannelSetupProviders,
    filterTiming: ChannelFilterTiming,
  ): void {
    forkJoin({
      smsCatalog: this.channelSetupService.listCatalogTemplates(brandId, 'sms').pipe(catchError(() => of([]))),
      emailCatalog: this.channelSetupService.listCatalogTemplates(brandId, 'email').pipe(catchError(() => of([]))),
      whatsappCatalog: this.channelSetupService.listCatalogTemplates(brandId, 'whatsapp').pipe(catchError(() => of([]))),
    }).subscribe({
      next: ({ smsCatalog, emailCatalog, whatsappCatalog }) => {
        this.applyLoadedSetup(config, providers, filterTiming, {
          sms: smsCatalog,
          email: emailCatalog,
          whatsapp: whatsappCatalog,
        });
      },
      error: () => {
        this.applyLoadedSetup(config, providers, filterTiming);
      },
    });
  }

  private applyLoadedSetup(
    config: ChannelSetupConfig,
    providers: ChannelSetupProviders,
    filterTiming: ChannelFilterTiming,
    catalogs?: ChannelSetupConfig['template_catalogs'],
  ): void {
    this.config = this.normalizeConfig(config);
    this.providers = providers ?? this.providers;
    this.catalogSms = catalogs?.sms || [];
    this.catalogEmail = catalogs?.email || [];
    this.catalogWhatsapp = catalogs?.whatsapp || [];
    this.config.attempts.forEach((attempt) => {
      (['sms', 'email', 'whatsapp'] as ChannelKey[]).forEach((channel) => {
        Object.keys(attempt.templates?.[channel] || {}).forEach((key) => {
          const value = String(key || '').trim();
          if (!value || LANGUAGE_OPTIONS.some((lang) => lang.key === value)) {
            return;
          }
          LANGUAGE_OPTIONS.unshift({
            key: value,
            name: value,
            native_name: value,
            short: languageShortCode(value),
          });
        });
      });
    });
    [...this.catalogSms, ...this.catalogEmail, ...this.catalogWhatsapp].forEach((item) => {
      const value = String(item.template_language || '').trim();
      if (!value || LANGUAGE_OPTIONS.some((lang) => lang.key === value)) {
        return;
      }
      LANGUAGE_OPTIONS.unshift({
        key: value,
        name: value,
        native_name: value,
        short: languageShortCode(value),
      });
    });
    this.filterTiming = filterTiming || emptyFilterTiming();
    this.hydrateOnDemandCatalogCopy();
    this.selectedAttemptIndex = 0;
    this.syncMessageTab();
    this.selectDefaultLanguageForActiveChannel();
    this.loading = false;
    this.markAttemptsPersisted();
    this.scheduleAttemptFootLabel();
    this.changeDetector.markForCheck();
  }

  private hydrateOnDemandCatalogCopy(): void {
    if (!this.isOnDemand) {
      return;
    }
    this.config.attempts.forEach((attempt) => {
      Object.values(attempt.templates?.sms || {}).forEach((tpl) => {
        const id = String(tpl.content_template_id || tpl.sms_body || '').trim();
        if (!id) {
          return;
        }
        const hit = this.catalogSms.find((item) => (
          this.catalogPick(item, 'template_id', 'templateId') === id
          || this.catalogPick(item, 'id', '_id') === id
        ));
        const text = hit ? this.catalogPick(hit, 'template_text', 'templateText') : '';
        if (text) {
          tpl.content_template_text = text;
          tpl.sms_body = text;
          tpl.content_template_id = this.catalogPick(hit as CatalogTemplate, 'template_id', 'templateId') || id;
        }
      });
      Object.values(attempt.templates?.whatsapp || {}).forEach((tpl) => {
        const id = String(tpl.whatsapp_template_id || '').trim();
        if (!id) {
          return;
        }
        const hit = this.catalogWhatsapp.find((item) => (
          this.catalogPick(item, 'template_id', 'templateId') === id
          || this.catalogPick(item, 'id', '_id') === id
        ));
        const text = hit ? this.catalogPick(hit, 'template_text', 'templateText') : '';
        if (text) {
          tpl.whatsapp_body = text;
          tpl.whatsapp_template_name = this.catalogPick(hit as CatalogTemplate, 'template_name', 'templateName') || tpl.whatsapp_template_name;
        }
      });
    });
  }

  private persistSetup(payload: ChannelSetupConfig) {
    const brandId = this.brandId as string;
    return this.isOnDemand
      ? this.channelSetupService.saveOnDemand(brandId, payload)
      : this.channelSetupService.saveConfig(brandId, payload);
  }

  toggleAttemptChannel(key: ChannelKey, enabled: boolean): void {
    if (!this.selectedAttempt) {
      return;
    }
    if (enabled && !this.isChannelComplete(key)) {
      this.setupChannel(key);
      return;
    }
    if (enabled && !this.isOnDemand && !this.isAttemptChannelConfigured(key) && this.channelEditor !== key) {
      this.setupChannel(key);
      return;
    }
    const set = new Set(this.selectedAttempt.channels);
    if (enabled) {
      set.add(key);
      this.config.channels[key].enabled = true;
    } else {
      set.delete(key);
      if (this.channelEditor === key) {
        this.closeChannelEditor();
      }
    }
    this.selectedAttempt.channels = [...set];
    this.syncProjectChannelsFromAttempts();
    this.syncMessageTab();
    this.touchAttempt();
  }

  setupChannel(key: ChannelKey): void {
    this.openChannelEditor(key, 'all');
  }

  openChannelEditor(key: ChannelKey, focus: ChannelEditorFocus = 'all'): void {
    this.messageTab = key;
    this.channelEditor = key;
    this.channelEditorFocus = focus;
    this.syncMessageTab();
    this.editingLanguage = this.reviewLanguage(key);
    this.maybeLoadEmailPreview();
  }

  finishChannelSetup(): void {
    if (!this.applyChannelEditorToAttempt()) {
      return;
    }
    this.closeChannelEditor();
  }

  finishOrCloseEditor(): void {
    if (!this.canUpdate) {
      this.channelEditor = null;
      this.channelEditorFocus = 'all';
      return;
    }
    this.finishChannelSetup();
  }

  saveFromChannelEditor(): void {
    if (!this.applyChannelEditorToAttempt()) {
      return;
    }
    this.save();
  }

  private applyChannelEditorToAttempt(): boolean {
    if (!this.channelEditor) {
      return false;
    }
    if (this.channelEditorFocus === 'messages') {
      return true;
    }
    const key = this.channelEditor;
    if (key === 'sms' && this.config.channels.sms.sms_trim_enabled && (!this.smsTrimLengthValid || !this.smsTrimNameValid)) {
      this.showToast('ERROR', 'SMS Trim', this.channelSetupGap('sms'), 15000);
      return false;
    }
    if (key === 'email' && this.config.channels.email.from && !this.emailFromLooksValid) {
      this.showToast('ERROR', 'From address', this.channelSetupGap('email'), 15000);
      return false;
    }
    if (key === 'email' && this.config.channels.email.reply_to && !this.replyToLooksValid) {
      this.showToast('ERROR', 'Reply-to', this.channelSetupGap('email'), 15000);
      return false;
    }
    if (this.isOnDemand) {
      if (!this.isChannelComplete(key)) {
        this.showToast('ERROR', 'Finish this channel', this.channelSetupGap(key), 15000);
        return false;
      }
      this.toggleAttemptChannel(key, true);
      return true;
    }
    if (!this.isAttemptChannelConfigured(key)) {
      this.showToast('ERROR', 'Finish this channel', this.channelSetupGap(key), 15000);
      return false;
    }
    this.toggleAttemptChannel(key, true);
    return true;
  }

  closeChannelEditor(): void {
    const key = this.channelEditor;
    this.channelEditor = null;
    this.channelEditorFocus = 'all';
    if (!this.canUpdate) {
      return;
    }
    if (key && this.selectedAttempt && !this.isChannelComplete(key)) {
      this.selectedAttempt.channels = this.selectedAttempt.channels.filter((channel) => channel !== key);
      this.syncProjectChannelsFromAttempts();
      this.syncMessageTab();
      this.touchAttempt();
    }
  }

  setAttemptTiming(timing: 'relative' | 'absolute'): void {
    if (!this.selectedAttempt || !this.canUpdate) {
      return;
    }
    this.selectedAttempt.timing = timing;
    this.touchAttempt();
  }

  setAttemptBasedOn(value: 1 | 2): void {
    if (!this.selectedAttempt || !this.canUpdate) {
      return;
    }
    if (this.selectedAttemptIndex === 0) {
      this.selectedAttempt.based_on = 1;
      return;
    }
    this.selectedAttempt.based_on = value;
    this.touchAttempt();
  }

  get requestTriggerHint(): string {
    if (!this.selectedAttempt) {
      return '';
    }
    return this.countDelayFromDetail(this.selectedAttempt, this.selectedAttemptIndex);
  }

  countDelayFromLabel(attempt: ChannelSetupAttempt, index: number): string {
    if (index === 0) {
      return 'Request Received';
    }
    if (Number(attempt?.based_on) === 1) {
      return this.isOnDemand ? 'Backup date' : 'Invitation date';
    }
    return 'Last attempt date';
  }

  countDelayFromDetail(attempt: ChannelSetupAttempt, index: number): string {
    return this.countDelayFromParts(attempt, index).map((part) => part.text).join('');
  }

  countDelayFromParts(attempt: ChannelSetupAttempt, index: number): Array<{ text: string; strong?: boolean }> {
    if (this.isOnDemand) {
      const wait = this.delayWaitPhraseFor(attempt.delay_minutes, index);
      if (wait === 'immediately') {
        return [
          { text: 'Sends ' },
          { text: 'immediately', strong: true },
          { text: ' when backup is triggered.' },
        ];
      }
      return [
        { text: 'Sends ' },
        { text: wait, strong: true },
        { text: ' after backup is triggered.' },
      ];
    }
    const fromInvitation = index === 0 || Number(attempt?.based_on) === 1;
    const kind = index === 0
      ? (this.isOnDemand ? 'backup attempt' : 'invitation')
      : 'reminder';
    const firstDateLabel = this.isOnDemand ? 'Backup date' : 'Invitation date';
    if (attempt?.timing === 'absolute') {
      return fromInvitation
        ? [
            { text: 'A ' },
            { text: 'specific send time', strong: true },
            { text: ' is set. ' },
            { text: index === 0 ? 'Request Received' : firstDateLabel, strong: true },
            { text: ' is still saved as the starting point.' },
          ]
        : [
            { text: 'A ' },
            { text: 'specific send time', strong: true },
            { text: ' is set. ' },
            { text: 'Last attempt date', strong: true },
            { text: ' is still saved as the starting point.' },
          ];
    }
    const wait = this.delayWaitPhraseFor(attempt.delay_minutes, index);
    if (fromInvitation) {
      if (wait === 'immediately') {
        return [
          { text: 'This ' },
          { text: kind, strong: true },
          { text: ' sends ' },
          { text: 'as soon as', strong: true },
          { text: ' the ' },
          { text: 'conversation is requested', strong: true },
          { text: '.' },
        ];
      }
      return [
        { text: 'This ' },
        { text: kind, strong: true },
        { text: ' sends ' },
        { text: wait, strong: true },
        { text: ' after the ' },
        { text: 'conversation is requested', strong: true },
        { text: '.' },
      ];
    }
    if (wait === 'immediately') {
      return [
        { text: 'This ' },
        { text: 'reminder', strong: true },
        { text: ' sends at the ' },
        { text: 'same moment', strong: true },
        { text: ' as the ' },
        { text: 'previous attempt', strong: true },
        { text: '.' },
      ];
    }
    return [
      { text: 'This ' },
      { text: 'reminder', strong: true },
      { text: ' sends ' },
      { text: wait, strong: true },
      { text: ' after the ' },
      { text: 'previous attempt', strong: true },
      { text: ' was sent.' },
    ];
  }

  private delayWaitPhraseFor(minutesValue: number | string | null | undefined, index = this.selectedAttemptIndex): string {
    const minutes = this.clampDelayMinutes(minutesValue, index);
    if (!minutes) {
      return 'immediately';
    }
    return this.delayHumanMessage(minutes, index).replace(/^Sends after /, '').replace(/\.$/, '');
  }

  private get delayWaitPhrase(): string {
    return this.delayWaitPhraseFor(this.selectedAttempt?.delay_minutes);
  }

  selectLanguage(lang: string): void {
    this.editingLanguage = lang;
    this.maybeLoadEmailPreview();
    this.changeDetector.markForCheck();
  }

  selectAttempt(index: number): void {
    this.selectedAttemptIndex = index;
    this.syncMessageTab();
    this.selectDefaultLanguageForActiveChannel();
    this.maybeLoadEmailPreview();
    this.closeChannelEditor();
    this.revealSelectedAttempt();
  }

  private bindAttemptListHeight(): void {
    const main = this.attemptMain?.nativeElement;
    const list = this.attemptList?.nativeElement;
    if (!main || !list) {
      this.attemptMainObserver?.disconnect();
      this.attemptMainObserver = undefined;
      return;
    }
    if (this.attemptMainObserver) {
      return;
    }
    this.attemptMainObserver = new ResizeObserver(() => {
      this.syncAttemptListHeight();
      this.ngZone.run(() => this.measureLangRows());
    });
    this.attemptMainObserver.observe(main);
    this.attemptMainObserver.observe(list);
    this.syncAttemptListHeight();
  }

  private langRowKey(attemptIndex: number, channel: ChannelKey): string {
    return `${attemptIndex}:${channel}`;
  }

  private measureLangRows(): void {
    if (this.destroyed) {
      return;
    }
    const list = this.attemptList?.nativeElement;
    if (!list) {
      return;
    }
    let changed = false;
    list.querySelectorAll<HTMLElement>('[data-lang-row]').forEach((row) => {
      const key = row.getAttribute('data-lang-row') || '';
      const splitAt = key.indexOf(':');
      const attempt = this.config.attempts[Number(key.slice(0, splitAt))];
      const channel = key.slice(splitAt + 1) as ChannelKey;
      const shorts = attempt ? this.channelLangShorts(channel, attempt) : [];
      const signature = shorts.map((item) => `${item.code}${item.isDefault ? '*' : ''}`).join('|');
      const known = this.langFitSignatures.get(key);
      const currentFit = this.langFitCounts.get(key);
      if (known !== signature) {
        this.langFitSignatures.set(key, signature);
        if (currentFit != null && currentFit < shorts.length) {
          this.langFitCounts.delete(key);
          changed = true;
          return;
        }
      }
      const nextFit = this.langPillsThatFit(row, shorts);
      if (currentFit !== nextFit) {
        this.langFitCounts.set(key, nextFit);
        changed = true;
      }
    });
    if (changed) {
      this.changeDetector.markForCheck();
    }
  }

  private langPillsThatFit(row: HTMLElement, shorts: Array<{ code: string; isDefault: boolean }>): number {
    const available = row.clientWidth;
    if (available <= 0 || !shorts.length) {
      return shorts.length;
    }
    const gap = 4;
    const moreWidth = 28;
    const pills = Array.from(row.querySelectorAll<HTMLElement>('em:not(.is-more)'));
    const widths = shorts.map((lang, index) => {
      const cacheKey = `${lang.code}${lang.isDefault ? '*' : ''}`;
      const measured = pills[index]?.offsetWidth || 0;
      if (measured > 0) {
        this.pillWidths.set(cacheKey, measured);
      }
      return measured || this.pillWidths.get(cacheKey) || 32;
    });
    let used = 0;
    let fit = 0;
    for (let index = 0; index < widths.length; index += 1) {
      const next = used + (fit > 0 ? gap : 0) + widths[index];
      const hiddenAfter = widths.length - (index + 1);
      const total = hiddenAfter > 0 ? next + gap + moreWidth : next;
      if (total > available + 1) {
        break;
      }
      used = next;
      fit += 1;
    }
    return Math.max(1, fit);
  }

  private syncAttemptListHeight(): void {
    const main = this.attemptMain?.nativeElement;
    const list = this.attemptList?.nativeElement;
    const sidebar = list?.parentElement;
    if (!main || !list || !sidebar) {
      return;
    }
    const chrome = sidebar.offsetHeight - list.offsetHeight;
    const available = main.offsetHeight - chrome;
    list.style.maxHeight = `${Math.max(180, available)}px`;
  }

  private revealSelectedAttempt(): void {
    setTimeout(() => {
      const list = this.attemptList?.nativeElement;
      const active = list?.querySelector('.ns-seq.is-active') as HTMLElement | null;
      if (!list || !active) {
        return;
      }
      const listRect = list.getBoundingClientRect();
      const itemRect = active.getBoundingClientRect();
      const pad = 8;
      let next = list.scrollTop;
      if (itemRect.top < listRect.top + pad) {
        next -= listRect.top + pad - itemRect.top;
      } else if (itemRect.bottom > listRect.bottom - pad) {
        next += itemRect.bottom - (listRect.bottom - pad);
      } else {
        return;
      }
      list.scrollTo({ top: Math.max(0, next), behavior: 'smooth' });
    });
  }

  setMessageTab(tab: ChannelKey): void {
    this.messageTab = tab;
    this.selectDefaultLanguageForActiveChannel();
  }

  openPreview(channel?: ChannelKey): void {
    const tabs = this.channelEditor
      ? [this.channelEditor]
      : this.attemptMessageTabs;
    const requested = channel || this.channelEditor || this.messageTab;
    this.previewChannel = tabs.includes(requested) ? requested : (tabs[0] || 'sms');
    this.previewOpen = true;
    this.previewDevice = this.previewChannel === 'email' ? 'desktop' : 'mobile';
    this.ensurePreviewDevice();
    this.refreshEmailPreviewSrc();
    this.maybeLoadEmailPreview();
  }

  closePreview(): void {
    this.previewOpen = false;
    this.revokeEmailPreview();
  }

  setPreviewChannel(channel: ChannelKey): void {
    if (!this.previewChannelTabs.includes(channel)) {
      return;
    }
    this.previewChannel = channel;
    this.ensurePreviewDevice();
    this.refreshEmailPreviewSrc();
    this.maybeLoadEmailPreview();
  }

  setPreviewDevice(device: PreviewDevice): void {
    if (!this.previewDevices.some((option) => option.id === device)) {
      return;
    }
    this.previewDevice = device;
  }

  channelLabel(channel: ChannelKey): string {
    if (channel === 'sms') {
      return 'SMS';
    }
    if (channel === 'email') {
      return 'Email';
    }
    return 'WhatsApp';
  }

  goToCreateTemplate(channel: ChannelKey): void {
    this.channelEditor = null;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        active_tab: 'templates',
        template_channel: channel,
        create_template: '1',
      },
      queryParamsHandling: 'merge',
    });
  }

  goToFiltersAndTiming(): void {
    this.channelEditor = null;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { active_tab: 'filters' },
      queryParamsHandling: 'merge',
    });
  }

  channelIcon(channel: ChannelKey): string {
    if (channel === 'sms') {
      return 'fa-commenting';
    }
    if (channel === 'email') {
      return 'fa-envelope';
    }
    return 'fa-whatsapp';
  }

  get previewDevices(): Array<{ id: PreviewDevice; label: string; hint: string }> {
    if (this.previewChannel === 'email') {
      return [{ id: 'desktop', label: 'Inbox', hint: 'Inbox' }];
    }
    return [
      { id: 'mobile', label: 'Phone', hint: 'Phone' },
      { id: 'tablet', label: 'Tablet', hint: 'Tablet' },
    ];
  }

  get previewViewportWidth(): number {
    if (this.previewDevice === 'desktop') {
      return 1024;
    }
    if (this.previewDevice === 'tablet') {
      return 768;
    }
    return 390;
  }

  get previewViewportHeight(): number {
    if (this.previewDevice === 'desktop') {
      return 720;
    }
    if (this.previewDevice === 'tablet') {
      return 1024;
    }
    return 844;
  }

  get previewScale(): number {
    const stage = 400;
    return Math.min(1, stage / this.previewViewportWidth);
  }

  get previewViewportLabel(): string {
    return this.previewDevices.find((option) => option.id === this.previewDevice)?.hint || '';
  }

  private ensurePreviewDevice(): void {
    if (!this.previewDevices.some((option) => option.id === this.previewDevice)) {
      this.previewDevice = this.previewDevices[0]?.id || 'mobile';
    }
  }

  saveEmailHtml(): void {
    if (!this.canUpdate) {
      this.showToast(
        this.isOverAttemptLimit ? 'INFO' : 'ERROR',
        this.isOverAttemptLimit ? 'Cannot update' : 'Not authorized',
        this.isOverAttemptLimit ? this.attemptOverLimitMessage : 'Not authorized',
      );
      return;
    }
    const html = (this.currentTemplate.email_body || '').trim();
    if (!html) {
      this.showToast('ERROR', 'Empty template', 'Write or paste HTML before saving.', 15000);
      return;
    }
    if (html.length > FIELD_MAX.emailHtml) {
      this.showToast('ERROR', 'HTML too large', `Keep the HTML under ${FIELD_MAX.emailHtml} characters.`, 15000);
      return;
    }
    this.prepareEmailHtml(html, this.currentTemplate.template_file_name || 'email-template.html');
  }

  /** Inline HTML editor (textarea + Save HTML). Hidden until we need it again. */
  readonly showEmailHtmlEditor = false;

  get hasEmailTemplate(): boolean {
    const tpl = this.currentTemplate;
    return Boolean(
      tpl?.email_body?.trim()
      || tpl?.email_template_id
      || tpl?.template_file_id
      || tpl?.template_name?.trim(),
    );
  }

  get emailHtmlPlaceholder(): string {
    if (this.loadingEmailHtml) {
      return 'Loading saved HTML…';
    }
    return 'Paste or edit HTML. Use {{customerName}} and {{feedbackUrl}}.';
  }

  get canSaveEmailHtml(): boolean {
    if (!this.canUpdate || this.uploadingEmail || this.loadingEmailHtml || this.saving) {
      return false;
    }
    const html = (this.currentTemplate.email_body || '').trim();
    if (!html) {
      return false;
    }
    return html !== this.emailHtmlSnapshot();
  }

  private emailHtmlSnapshotKey(attemptIndex = this.selectedAttemptIndex, lang = this.currentLanguage): string {
    return `${attemptIndex}:${lang}`;
  }

  private emailHtmlSnapshot(): string {
    return this.emailHtmlSnapshots[this.emailHtmlSnapshotKey()] || '';
  }

  private rememberEmailHtmlSnapshot(html: string, attemptIndex = this.selectedAttemptIndex, lang = this.currentLanguage): void {
    this.emailHtmlSnapshots[this.emailHtmlSnapshotKey(attemptIndex, lang)] = (html || '').trim();
  }

  viewEmailTemplate(): void {
    this.withEmailHtml((html) => {
      this.openHtmlInNewTab(this.wrapEmailDocument(this.decodeEmailHtml(html)));
    });
  }

  previewEmailTemplate(): void {
    if (!this.hasEmailTemplate) {
      this.showToast('ERROR', 'No template', 'There is no HTML template to preview yet.', 15000);
      return;
    }
    this.messageTab = 'email';
    this.openPreview('email');
  }

  downloadEmailTemplate(): void {
    this.withEmailHtml((html) => {
      this.saveHtmlFile(html, this.currentTemplate.template_file_name || 'email-template.html');
    });
  }

  private withEmailHtml(next: (html: string) => void): void {
    if (!this.hasEmailTemplate) {
      this.showToast('ERROR', 'No template', 'There is no HTML template yet.', 15000);
      return;
    }
    const html = (this.currentTemplate.email_body || '').trim();
    if (html) {
      next(html);
      return;
    }
    const request$ = this.emailHtmlRequest(this.currentTemplate);
    if (!request$) {
      this.showToast('ERROR', 'No template', 'There is no HTML template yet.', 15000);
      return;
    }
    request$.subscribe({
      next: (template) => {
        const body = (template?.html || '').trim();
        if (!body) {
          this.showToast('ERROR', 'Empty template', 'The saved HTML template is empty.', 15000);
          return;
        }
        this.currentTemplate.email_body = body;
        if (template?.file_name && !this.currentTemplate.template_file_name) {
          this.currentTemplate.template_file_name = template.file_name;
        }
        next(body);
      },
      error: (error) => {
        this.showToast(
          'ERROR',
          'Could not load template',
          error?.error?.message || error?.message || 'Could not load the email template',
          15000,
        );
      },
    });
  }

  private openHtmlInNewTab(html: string): void {
    if (!html.trim()) {
      this.showToast('ERROR', 'Empty template', 'The HTML template is empty.', 15000);
      return;
    }
    const win = window.open('about:blank', '_blank');
    if (!win) {
      this.showToast('ERROR', 'Popup blocked', 'Allow popups to view the HTML template in a new tab.', 15000);
      return;
    }
    void this.loadEmailHtmlWindow(html, win);
  }

  private saveHtmlFile(html: string, fileName: string): void {
    const safeName = /\.html?$/i.test(fileName) ? fileName : `${fileName}.html`;
    const link = document.createElement('a');
    const url = URL.createObjectURL(new Blob([html], { type: 'text/html' }));
    link.href = url;
    link.download = safeName;
    link.click();
    URL.revokeObjectURL(url);
  }

  languageLabel(key: string): string {
    const match = LANGUAGE_OPTIONS.find((option) => option.key === key);
    if (match) {
      return match.name;
    }
    return key.replace(/_/g, ' ');
  }

  defaultLanguageFor(channel: ChannelKey = this.activeTemplateChannel, attempt = this.selectedAttempt): string {
    const langs = Object.keys(attempt?.templates?.[channel] || {});
    const stored = attempt?.default_languages?.[channel];
    if (stored && langs.includes(stored)) {
      return stored;
    }
    return langs[0] || '';
  }

  isDefaultLanguage(lang: string, channel: ChannelKey = this.activeTemplateChannel): boolean {
    return this.defaultLanguageFor(channel) === lang;
  }

  setDefaultLanguage(lang: string): void {
    const attempt = this.selectedAttempt;
    if (!attempt || !this.canUpdate) {
      return;
    }
    const bag = this.channelTemplateBag();
    if (!bag[lang]) {
      return;
    }
    if (!attempt.default_languages) {
      attempt.default_languages = { sms: '', email: '', whatsapp: '' };
    }
    attempt.default_languages[this.activeTemplateChannel] = lang;
    this.maybeLoadEmailPreview();
    this.touchAttempt();
  }

  private selectDefaultLanguageForActiveChannel(): void {
    this.editingLanguage = this.defaultLanguageFor(this.activeTemplateChannel);
  }

  languageAddBlockedMessage(): string {
    const key = this.activeTemplateChannel;
    const bag = this.channelTemplateBag();
    const langs = Object.keys(bag);
    if (!langs.length) {
      return '';
    }
    const missing = langs.find((lang) => {
      const tpl = bag[lang];
      if (key === 'sms') {
        return this.isOnDemand
          ? !String(tpl?.content_template_id || '').trim()
          : !String(tpl?.sms_body || '').trim();
      }
      if (key === 'email') {
        if (this.isOnDemand) {
          return !String(tpl?.email_template_id || '').trim()
            && !String(tpl?.template_name || '').trim()
            && !String(tpl?.template_file_id || '').trim();
        }
        return !this.inviteEmailReady(tpl);
      }
      if (!String(tpl?.whatsapp_template_id || '').trim()) {
        return true;
      }
      return this.isOnDemand ? false : !String(tpl?.whatsapp_body || '').trim();
    });
    if (!missing) {
      return '';
    }
    const who = this.languageLabel(missing);
    if (key === 'sms') {
      return this.isOnDemand
        ? `Select an SMS template for ${who} before adding another language.`
        : `Write the SMS message for ${who} before adding another language.`;
    }
    if (key === 'email') {
      if (this.isOnDemand) {
        return `Select an email template for ${who} before adding another language.`;
      }
      const tpl = bag[missing];
      const needsSubject = !String(tpl?.email_subject || '').trim();
      const needsTemplate = !this.hasEmailTemplateFor(tpl);
      if (needsSubject && needsTemplate) {
        return `Add an email subject and HTML template for ${who} before adding another language.`;
      }
      if (needsSubject) {
        return `Add an email subject for ${who} before adding another language.`;
      }
      return `Upload an HTML template for ${who} before adding another language.`;
    }
    const tpl = bag[missing];
    const needsId = !String(tpl?.whatsapp_template_id || '').trim();
    const needsText = !this.isOnDemand && !String(tpl?.whatsapp_body || '').trim();
    if (needsId && needsText) {
      return `Select a WhatsApp template for ${who} before adding another language.`;
    }
    if (needsId) {
      return `Select a WhatsApp Template Id for ${who} before adding another language.`;
    }
    return `Select a WhatsApp template that has template text for ${who} before adding another language.`;
  }

  addLanguage(): void {
    if (!this.canUpdate || this.languageAddBlockedMessage()) {
      return;
    }
    const lang = this.newLanguage.trim().toLowerCase().replace(/\s+/g, '_');
    if (!lang) {
      return;
    }
    const bag = this.channelTemplateBag();
    const isFirst = Object.keys(bag).length === 0;
    if (!bag[lang]) {
      bag[lang] = emptyTemplate();
    }
    this.editingLanguage = lang;
    this.newLanguage = '';
    if (isFirst) {
      this.setDefaultLanguage(lang);
      return;
    }
    this.maybeLoadEmailPreview();
    this.touchAttempt();
  }

  askRemoveLanguage(lang: string): void {
    if (!this.canUpdate) {
      return;
    }
    const name = this.languageLabel(lang);
    this.openConfirm({
      title: `Remove ${name}?`,
      message: `The ${name} template on this attempt will be removed.`,
      tone: 'danger',
      onConfirm: () => this.removeLanguage(lang),
    });
  }

  removeLanguage(lang: string): void {
    if (!this.canUpdate) {
      return;
    }
    const bag = this.channelTemplateBag();
    if (!bag[lang]) {
      return;
    }
    const wasDefault = this.isDefaultLanguage(lang);
    delete bag[lang];
    const remaining = Object.keys(bag);
    const attempt = this.selectedAttempt;
    if (attempt?.default_languages && wasDefault) {
      attempt.default_languages[this.activeTemplateChannel] = remaining[0] || '';
    }
    if (this.editingLanguage === lang) {
      this.editingLanguage = this.defaultLanguageFor(this.activeTemplateChannel);
    }
    this.maybeLoadEmailPreview();
    this.touchAttempt();
  }

  startSetup(): void {
    if (!this.canUpdate) {
      return;
    }
    if (!this.config.attempts.length) {
      this.config.attempts.push(emptyAttempt(1));
    }
    this.selectedAttemptIndex = 0;
    this.syncMessageTab();
    this.selectDefaultLanguageForActiveChannel();
  }

  addAttempt(): void {
    if (!this.canUpdate) {
      return;
    }
    if (!this.canAddAttempt) {
      this.showToast('INFO', 'Cannot add attempt', this.addAttemptHint, 15000);
      return;
    }
    if (!this.config.attempts.length) {
      this.startSetup();
      return;
    }
    const nextDelay = this.clampOnDemandCreateDelay(REMINDER_DEFAULT_DELAY_MINUTES);
    this.config.attempts.push({
      ...emptyAttempt(this.config.attempts.length + 1),
      delay_minutes: nextDelay,
    });
    this.selectedAttemptIndex = this.config.attempts.length - 1;
    this.syncMessageTab();
    this.selectDefaultLanguageForActiveChannel();
    this.revealSelectedAttempt();
  }

  duplicateAttempt(index: number): void {
    if (!this.canUpdate) {
      return;
    }
    const existing = this.findExistingDuplicateIndex(index);
    if (existing >= 0) {
      this.selectAttempt(existing);
      this.showToast(
        'INFO',
        'Duplicate already exists',
        `${this.attemptDisplayName(existing)} is already a copy of ${this.attemptDisplayName(index)}.`,
      );
      return;
    }
    if (!this.canDuplicateAttempt(index)) {
      this.showToast('INFO', 'Cannot duplicate', this.duplicateAttemptHint, 15000);
      return;
    }
    const sourceName = this.attemptDisplayName(index);
    const nextName = this.attemptDisplayName(this.config.attempts.length);
    this.openConfirm({
      title: `Duplicate ${sourceName}`,
      message: `Create ${nextName} as a copy of ${sourceName}? Channels and messages are copied. The delay starts at 1 day.`,
      tone: 'ok',
      onConfirm: () => this.applyDuplicateAttempt(index, false),
      extraAction: {
        label: 'Yes, continue and save',
        onClick: () => this.applyDuplicateAttempt(index, true),
      },
    });
  }

  private applyDuplicateAttempt(index: number, persist: boolean): void {
    const source = this.config.attempts[index];
    if (!source) {
      return;
    }
    this.config.attempts.push({
      ..._.cloneDeep(source),
      attempt: this.config.attempts.length + 1,
      timing: 'relative',
      delay_minutes: this.clampOnDemandCreateDelay(REMINDER_DEFAULT_DELAY_MINUTES),
      absolute_at: '',
      based_on: Number(source.based_on) === 1 ? 1 : 2,
    });
    this.config.attempts.forEach((attempt, i) => {
      attempt.attempt = i + 1;
    });
    this.selectedAttemptIndex = this.config.attempts.length - 1;
    this.syncMessageTab();
    this.selectDefaultLanguageForActiveChannel();
    this.revealSelectedAttempt();
    if (persist) {
      this.save();
    }
  }

  hasExistingDuplicate(index: number): boolean {
    return this.findExistingDuplicateIndex(index) >= 0;
  }

  private findExistingDuplicateIndex(sourceIndex: number): number {
    const source = this.config.attempts[sourceIndex];
    if (!source) {
      return -1;
    }
    const fingerprint = this.duplicateFingerprint(source);
    for (let i = this.config.attempts.length - 1; i > sourceIndex; i--) {
      if (this.duplicateFingerprint(this.config.attempts[i]) === fingerprint) {
        return i;
      }
    }
    return -1;
  }

  private duplicateFingerprint(attempt: ChannelSetupAttempt): string {
    return JSON.stringify({
      channels: [...(attempt.channels || [])].map(String).sort(),
      templates: this.templateSnapshot(attempt.templates),
      default_languages: {
        sms: attempt.default_languages?.sms || '',
        email: attempt.default_languages?.email || '',
        whatsapp: attempt.default_languages?.whatsapp || '',
      },
    });
  }

  removeAttempt(index: number): void {
    if (!this.canUpdate) {
      return;
    }
    if (!this.canRemoveAttempt(index)) {
      this.showToast(
        'INFO',
        index === 0 ? `Cannot delete ${this.attemptDisplayName(0)}` : 'Cannot delete',
        this.removeAttemptHint,
        15000,
      );
      return;
    }
    const name = this.attemptDisplayName(index);
    const saved = this.isAttemptSaved(index);
    this.openConfirm({
      title: `Delete ${name}`,
      message: saved
        ? `Delete ${name} from this sequence? This is saved and will stop sending after you confirm.`
        : `${name} has not been saved. Delete it from this sequence?`,
      tone: 'danger',
      onConfirm: () => this.applyRemoveAttempt(index, saved),
    });
  }

  private applyRemoveAttempt(index: number, persist: boolean): void {
    if (!this.config.attempts.length || index <= 0 || index !== this.config.attempts.length - 1) {
      return;
    }
    const removedName = this.attemptDisplayName(index);
    const removed = this.config.attempts[index];
    this.config.attempts.splice(index, 1);
    this.config.attempts.forEach((attempt, i) => {
      attempt.attempt = i + 1;
    });
    this.selectedAttemptIndex = this.config.attempts.length
      ? Math.min(this.selectedAttemptIndex, this.config.attempts.length - 1)
      : 0;
    this.syncProjectChannelsFromAttempts();
    this.syncMessageTab();
    this.selectDefaultLanguageForActiveChannel();
    if (!persist) {
      return;
    }
    this.persistRemovedAttempt(removed, index, removedName);
  }

  private persistRemovedAttempt(removed: ChannelSetupAttempt, index: number, removedName: string): void {
    if (!this.brandId) {
      return;
    }
    const payload = this.buildPayloadForAttempts(this.config.attempts);
    this.saving = true;
    this.persistSetup(payload).subscribe({
      next: (saved) => {
        const normalized = this.normalizeConfig(saved);
        this.config = {
          ...normalized,
          attempts: this.config.attempts,
        };
        this.saving = false;
        this.syncMessageTab();
        this.markAttemptsPersisted();
        this.showToast('SUCCESS', 'Deleted', `${removedName} was deleted.`, 15000);
      },
      error: (error) => {
        this.config.attempts.splice(index, 0, removed);
        this.config.attempts.forEach((attempt, i) => {
          attempt.attempt = i + 1;
        });
        this.selectedAttemptIndex = index;
        this.syncProjectChannelsFromAttempts();
        this.syncMessageTab();
        this.saving = false;
        this.showToast(
          'ERROR',
          'Could not delete',
          firstValidationMessage(error) || `Failed to delete ${removedName}`,
          15000,
        );
      },
    });
  }

  get maxAttemptsAllowed(): number {
    return this.isOnDemand ? MAX_BACKUP_ATTEMPTS : MAX_LIVE_ATTEMPTS;
  }

  get isAtAttemptLimit(): boolean {
    return this.config.attempts.length >= this.maxAttemptsAllowed;
  }

  get isOverAttemptLimit(): boolean {
    return this.config.attempts.length > this.maxAttemptsAllowed;
  }

  get canUpdate(): boolean {
    return this.isWriteAllowed && !this.isOverAttemptLimit && this.conversationReady;
  }

  get attemptLimitMessage(): string {
    if (this.isOnDemand) {
      return `Backup Attempt allows a maximum of ${MAX_BACKUP_ATTEMPTS} attempts. To add more, contact the Service team.`;
    }
    return `Invites & reminders allow a maximum of ${MAX_LIVE_ATTEMPTS} attempts, including the invitation. To add more, contact the Service team.`;
  }

  get attemptOverLimitMessage(): string {
    if (this.isOnDemand) {
      return `This Backup Attempt sequence has more than ${MAX_BACKUP_ATTEMPTS} attempts. It is loaded for viewing only. Contact the Service team to change it.`;
    }
    return `This invites & reminders sequence has more than ${MAX_LIVE_ATTEMPTS} attempts. It is loaded for viewing only. Contact the Service team to change it.`;
  }

  get addAttemptHint(): string {
    if (this.isOverAttemptLimit) {
      return this.attemptOverLimitMessage;
    }
    if (this.isAtAttemptLimit) {
      return this.attemptLimitMessage;
    }
    if (this.canAddAttempt) {
      return 'Add a new last attempt';
    }
    if (this.attemptsPastSendWindow.length) {
      return 'This sequence already sends after request processing period or link expiration. Shorten those delays, or open Filters & timing, before adding another attempt.';
    }
    if (this.hasUnsavedLastAttempt) {
      return 'Save or remove the unsaved last attempt before adding another.';
    }
    if (!this.config.attempts.every((attempt) => this.attemptHasAnyConfiguredChannel(attempt))) {
      return 'Finish setup for the current attempts, then save, before adding another.';
    }
    return 'Save the current attempts before adding another.';
  }

  get canAddAttempt(): boolean {
    if (!this.canUpdate || this.loading || this.saving) {
      return false;
    }
    if (!this.config.attempts.length) {
      return false;
    }
    if (this.isAtAttemptLimit) {
      return false;
    }
    if (this.attemptsPastSendWindow.length) {
      return false;
    }
    if (this.hasUnsavedLastAttempt) {
      return false;
    }
    return this.config.attempts.every((attempt) => this.attemptHasAnyConfiguredChannel(attempt));
  }

  get canDuplicateSelectedAttempt(): boolean {
    return this.canDuplicateAttempt(this.selectedAttemptIndex);
  }

  canDuplicateAttempt(index: number): boolean {
    if (!this.canUpdate || this.loading || this.saving) {
      return false;
    }
    if (this.isAtAttemptLimit) {
      return false;
    }
    if (!this.isAttemptSaved(index)) {
      return false;
    }
    if (this.hasUnsavedLastAttempt) {
      return false;
    }
    if (this.attemptsPastSendWindow.length) {
      return false;
    }
    const source = this.config.attempts[index];
    return Boolean(source && this.attemptHasAnyConfiguredChannel(source));
  }

  get duplicateAttemptHint(): string {
    if (this.isOverAttemptLimit) {
      return this.attemptOverLimitMessage;
    }
    if (this.isAtAttemptLimit) {
      return this.attemptLimitMessage;
    }
    if (this.hasExistingDuplicate(this.selectedAttemptIndex)) {
      return 'Open the existing copy of this attempt';
    }
    if (this.canDuplicateSelectedAttempt) {
      return 'Create a new last attempt from this saved one';
    }
    if (this.attemptsPastSendWindow.length) {
      return 'This sequence already sends after request processing period or link expiration. Shorten those delays, or open Filters & timing, before duplicating.';
    }
    if (this.hasUnsavedLastAttempt) {
      return 'Save or remove the unsaved last attempt before duplicating';
    }
    if (!this.isAttemptSaved(this.selectedAttemptIndex)) {
      return 'Save this attempt before duplicating it';
    }
    return 'Finish and save this attempt before duplicating';
  }

  get canRemoveSelectedAttempt(): boolean {
    return this.canRemoveAttempt(this.selectedAttemptIndex);
  }

  get removeAttemptHint(): string {
    if (this.selectedAttemptIndex === 0) {
      return this.isOnDemand
        ? 'The first backup attempt cannot be deleted. If you do not want it to send, remove it from use when triggering a request.'
        : 'The first invitation attempt cannot be deleted. If you do not want invitations to send, turn the notification service off instead.';
    }
    if (this.canRemoveSelectedAttempt) {
      return this.isAttemptSaved(this.selectedAttemptIndex)
        ? 'Delete this last reminder'
        : 'Delete this unsaved reminder';
    }
    return this.isOnDemand
      ? 'Only the last reminder can be deleted. Backup Attempt must stay.'
      : 'Only the last reminder can be deleted. Invitation must stay.';
  }

  canRemoveAttempt(index: number): boolean {
    if (!this.canUpdate || this.loading || this.saving) {
      return false;
    }
    if (!this.config.attempts.length || index === 0) {
      return false;
    }
    return index === this.config.attempts.length - 1;
  }

  private get hasUnsavedLastAttempt(): boolean {
    return this.config.attempts.length > this.persistedAttemptCount;
  }

  private attemptHasAnyConfiguredChannel(attempt: ChannelSetupAttempt): boolean {
    return this.deliveryChannels.some((key) =>
      (attempt.channels || []).includes(key) && this.attemptHasChannelReady(attempt, key),
    );
  }

  isAttemptSaved(index: number): boolean {
    return index >= 0 && index < this.persistedAttemptCount;
  }

  isAttemptChanged(index: number): boolean {
    if (this.attemptChangedCacheGen !== this.dirtyGen) {
      this.attemptChangedCacheGen = this.dirtyGen;
      this.attemptChangedCache = this.config.attempts.map((_, i) => this.computeAttemptChanged(i));
    }
    return Boolean(this.attemptChangedCache[index]);
  }

  private computeAttemptChanged(index: number): boolean {
    if (!this.isAttemptSaved(index)) {
      return false;
    }
    const attempt = this.config.attempts[index];
    return Boolean(attempt) && this.attemptSnapshotJson(attempt) !== this.persistedAttemptJson[index];
  }

  isDelayFieldDirty(): boolean {
    const attempt = this.selectedAttempt;
    const saved = this.persistedAttempts[this.selectedAttemptIndex];
    if (!attempt || !saved) {
      return false;
    }
    return Number(attempt.delay_minutes) !== Number(saved.delay_minutes);
  }

  isAbsoluteAtDirty(): boolean {
    const attempt = this.selectedAttempt;
    if (!attempt || attempt.timing !== 'absolute') {
      return false;
    }
    const saved = this.persistedAttempts[this.selectedAttemptIndex];
    if (!saved) {
      return false;
    }
    return String(attempt.absolute_at || '') !== String(saved.absolute_at || '');
  }

  isSharedDirty(channel: ChannelKey, field: string): boolean {
    const current = (this.config.channels as unknown as Record<string, Record<string, unknown>>)?.[channel]?.[field];
    const saved = (this.persistedChannels as unknown as Record<string, Record<string, unknown>> | null)?.[channel]?.[field];
    if (typeof current === 'boolean' || typeof saved === 'boolean') {
      return Boolean(current) !== Boolean(saved);
    }
    return String(current ?? '') !== String(saved ?? '');
  }

  isTemplateDirty(field: keyof ChannelSetupTemplate): boolean {
    const channel = (this.channelEditor || this.messageTab) as ChannelKey;
    const lang = this.currentLanguage;
    const current = String(this.currentTemplate?.[field] ?? '');
    const savedAttempt = this.persistedAttempts[this.selectedAttemptIndex];
    const saved = String(savedAttempt?.templates?.[channel]?.[lang]?.[field] ?? '');
    if (!savedAttempt) {
      return current !== String(emptyTemplate()[field] ?? '');
    }
    return current !== saved;
  }

  attemptStatusLabel(index: number): string {
    if (this.attemptDelayMissesWindow(index)) {
      return 'Too late';
    }
    if (!this.isAttemptSaved(index)) {
      return 'Not saved';
    }
    return this.isAttemptChanged(index) ? 'Unsaved changes' : 'Saved';
  }

  get hasSharedChannelChanges(): boolean {
    return this.channelsSnapshotJson() !== this.persistedChannelsJson;
  }

  get canSaveAttempt(): boolean {
    if (!this.canUpdate || this.loading || this.saving) {
      return false;
    }
    const index = this.selectedAttemptIndex;
    if (!this.isAttemptSaved(index)) {
      return true;
    }
    return this.isAttemptChanged(index) || this.hasSharedChannelChanges;
  }

  get canDiscardAttempt(): boolean {
    if (!this.canUpdate || this.loading || this.saving || !this.selectedAttempt) {
      return false;
    }
    const index = this.selectedAttemptIndex;
    return !this.isAttemptSaved(index)
      || this.isAttemptChanged(index)
      || this.hasSharedChannelChanges;
  }

  discardAttemptChanges(): void {
    if (!this.canDiscardAttempt) {
      return;
    }
    const name = this.attemptDisplayName(this.selectedAttemptIndex);
    const unsaved = !this.isAttemptSaved(this.selectedAttemptIndex);
    this.openConfirm({
      title: unsaved ? `Discard ${name}` : `Discard changes to ${name}`,
      message: unsaved
        ? `${name} has not been saved. Discard it and return to the last saved sequence?`
        : `Discard unsaved changes to ${name}? Timing, channels, and messages for this attempt go back to the last save. Shared sender settings also revert if you changed them.`,
      tone: 'danger',
      onConfirm: () => this.applyDiscardAttemptChanges(),
    });
  }

  private applyDiscardAttemptChanges(): void {
    const index = this.selectedAttemptIndex;
    const name = this.attemptDisplayName(index);
    const removingDraft = !this.isAttemptSaved(index) && index > 0 && index === this.config.attempts.length - 1;
    if (this.persistedChannels) {
      this.config.channels = _.cloneDeep(this.persistedChannels);
    }
    if (!this.isAttemptSaved(index)) {
      if (removingDraft) {
        this.config.attempts.splice(index, 1);
        this.selectedAttemptIndex = this.config.attempts.length - 1;
      } else {
        this.config.attempts[index] = this.persistedAttempts[index]
          ? _.cloneDeep(this.persistedAttempts[index])
          : emptyAttempt(index + 1);
      }
    } else if (this.persistedAttempts[index]) {
      this.config.attempts[index] = _.cloneDeep(this.persistedAttempts[index]);
    }
    this.attemptTouched = false;
    this.syncProjectChannelsFromAttempts();
    this.persistedChannels = _.cloneDeep(this.config.channels);
    this.persistedChannelsJson = this.channelsSnapshotJson();
    const restored = this.config.attempts[this.selectedAttemptIndex];
    if (restored && this.isAttemptSaved(this.selectedAttemptIndex)) {
      this.persistedAttempts[this.selectedAttemptIndex] = _.cloneDeep(restored);
      this.persistedAttemptJson[this.selectedAttemptIndex] = this.attemptSnapshotJson(restored);
    }
    this.bumpDirtyCache();
    this.syncMessageTab();
    this.selectDefaultLanguageForActiveChannel();
    this.maybeLoadEmailPreview();
    this.changeDetector.markForCheck();
    this.showToast(
      'INFO',
      'Changes discarded',
      removingDraft ? `${name} was discarded.` : `Reverted ${name} to the last save.`,
      15000,
    );
  }

  emailSubjectGap(attempt: ChannelSetupAttempt | undefined): string {
    if (!attempt?.channels?.includes('email')) {
      return '';
    }
    const bag = attempt.templates?.email || {};
    const langs = Object.keys(bag);
    if (this.isOnDemand) {
      if (!langs.length) {
        return 'Choose a language, then select an email template.';
      }
      const missing = langs.find((lang) => (
        !String(bag[lang]?.email_template_id || '').trim()
        && !String(bag[lang]?.template_name || '').trim()
        && !String(bag[lang]?.template_file_id || '').trim()
      ));
      if (!missing) {
        return '';
      }
      if (langs.length === 1) {
        return 'Select an email template before saving this attempt.';
      }
      return `Select an email template for ${this.languageLabel(missing)}.`;
    }
    if (!langs.length) {
      return 'Choose a language, then add an email subject and HTML template.';
    }
    const incomplete = langs.find((lang) => (
      !String(bag[lang]?.email_subject || '').trim() || !this.hasEmailTemplateFor(bag[lang])
    ));
    if (!incomplete) {
      return '';
    }
    const tpl = bag[incomplete];
    const needsSubject = !String(tpl?.email_subject || '').trim();
    const needsTemplate = !this.hasEmailTemplateFor(tpl);
    const who = langs.length === 1 ? '' : ` for ${this.languageLabel(incomplete)}`;
    if (needsSubject && needsTemplate) {
      return `Add an email subject and HTML template${who} before saving this attempt.`;
    }
    if (needsSubject) {
      return `Add an email subject${who} before saving this attempt.`;
    }
    return `Upload an HTML template${who} before saving this attempt.`;
  }

  smsTemplateGap(attempt: ChannelSetupAttempt | undefined): string {
    if (!attempt?.channels?.includes('sms')) {
      return '';
    }
    const bag = attempt.templates?.sms || {};
    const langs = Object.keys(bag);
    if (!langs.length) {
      return this.isOnDemand
        ? 'Choose a language, then select an SMS template.'
        : 'Choose a language, then write the SMS message.';
    }
    const missing = langs.find((lang) => (
      this.isOnDemand
        ? !String(bag[lang]?.content_template_id || '').trim()
        : !String(bag[lang]?.sms_body || '').trim()
    ));
    if (!missing) {
      return '';
    }
    const who = langs.length === 1 ? '' : ` for ${this.languageLabel(missing)}`;
    if (this.isOnDemand) {
      return `Select an SMS template${who} before saving this attempt.`;
    }
    return `Write the SMS message${who} before saving this attempt.`;
  }

  whatsappTemplateGap(attempt: ChannelSetupAttempt | undefined): string {
    if (!attempt?.channels?.includes('whatsapp')) {
      return '';
    }
    const bag = attempt.templates?.whatsapp || {};
    const langs = Object.keys(bag);
    if (!langs.length) {
      return 'Choose a language, then select a WhatsApp Template Id.';
    }
    const missing = langs.find((lang) => {
      const tpl = bag[lang];
      if (!String(tpl?.whatsapp_template_id || '').trim()) {
        return true;
      }
      return this.isOnDemand ? false : !String(tpl?.whatsapp_body || '').trim();
    });
    if (!missing) {
      return '';
    }
    const tpl = bag[missing];
    const needsId = !String(tpl?.whatsapp_template_id || '').trim();
    const needsText = !this.isOnDemand && !String(tpl?.whatsapp_body || '').trim();
    const who = langs.length === 1 ? '' : ` for ${this.languageLabel(missing)}`;
    if (needsId && needsText) {
      return `Select a WhatsApp template${who} before saving this attempt.`;
    }
    if (needsId) {
      return `Select a WhatsApp Template Id${who} before saving this attempt.`;
    }
    return `Select a WhatsApp template that has template text${who} before saving this attempt.`;
  }

  private markAttemptsPersisted(): void {
    this.attemptTouched = false;
    this.bumpDirtyCache();
    this.persistedAttemptCount = this.config.attempts.length;
    this.persistedChannelsJson = this.channelsSnapshotJson();
    this.persistedChannels = _.cloneDeep(this.config.channels);
    this.persistedAttempts = this.config.attempts.map((attempt) => _.cloneDeep(attempt));
    this.persistedAttemptJson = this.config.attempts.map((attempt) => this.attemptSnapshotJson(attempt));
  }

  private markSelectedAttemptPersisted(): void {
    const index = this.selectedAttemptIndex;
    const attempt = this.config.attempts[index];
    if (!attempt) {
      return;
    }
    this.attemptTouched = false;
    this.bumpDirtyCache();
    this.persistedChannelsJson = this.channelsSnapshotJson();
    this.persistedChannels = _.cloneDeep(this.config.channels);
    this.persistedAttempts[index] = _.cloneDeep(attempt);
    this.persistedAttemptJson[index] = this.attemptSnapshotJson(attempt);
    this.persistedAttemptCount = Math.max(this.persistedAttemptCount, index + 1);
  }

  private channelsSnapshotJson(): string {
    return JSON.stringify({
      email: {
        enabled: Boolean(this.config.channels.email.enabled),
        provider: this.config.channels.email.provider || '',
        from: this.config.channels.email.from || '',
        reply_to: this.config.channels.email.reply_to || '',
      },
      sms: {
        enabled: Boolean(this.config.channels.sms.enabled),
        sender_id: this.config.channels.sms.sender_id || '',
        provider: this.config.channels.sms.provider || '',
        international_provider: this.config.channels.sms.international_provider || '',
        sms_trim_enabled: Boolean(this.config.channels.sms.sms_trim_enabled),
        recipient_name_length: Number(this.config.channels.sms.recipient_name_length) || 0,
        default_recipient_name: this.config.channels.sms.default_recipient_name || '',
      },
      whatsapp: {
        enabled: Boolean(this.config.channels.whatsapp.enabled),
        sender_id: this.config.channels.whatsapp.sender_id || '',
        domestic_provider: this.config.channels.whatsapp.domestic_provider || '',
        international_provider: this.config.channels.whatsapp.international_provider || '',
      },
    });
  }

  private bumpDirtyCache(): void {
    this.dirtyGen += 1;
  }

  private compactSnapshotText(value: string): string {
    if (value.length <= 80) {
      return value;
    }
    return `${value.length}:${value.slice(0, 32)}:${value.slice(-32)}`;
  }

  private attemptSnapshotJson(attempt: ChannelSetupAttempt): string {
    return JSON.stringify({
      timing: attempt.timing === 'absolute' ? 'absolute' : 'relative',
      delay_minutes: Number(attempt.delay_minutes) || 0,
      absolute_at: attempt.absolute_at || '',
      based_on: Number(attempt.based_on) === 1 ? 1 : 2,
      channels: [...(attempt.channels || [])].map(String).sort(),
      templates: this.templateSnapshot(attempt.templates),
      default_languages: {
        sms: attempt.default_languages?.sms || '',
        email: attempt.default_languages?.email || '',
        whatsapp: attempt.default_languages?.whatsapp || '',
      },
    });
  }

  private templateSnapshot(templates: ChannelSetupAttempt['templates'] | undefined): unknown {
    const bags = templates || emptyChannelTemplates();
    const pack = (bag: Record<string, ChannelSetupTemplate> | undefined, fields: Array<keyof ChannelSetupTemplate>) => {
      const out: Record<string, unknown> = {};
      Object.keys(bag || {}).sort().forEach((lang) => {
        const tpl = (bag || {})[lang] || emptyTemplate();
        const row: Record<string, string> = {};
        fields.forEach((field) => {
          const raw = String(tpl[field] ?? '');
          row[String(field)] = this.compactSnapshotText(raw);
        });
        out[lang] = row;
      });
      return out;
    };
    return {
      sms: pack(bags.sms, ['sms_body', 'content_template_id']),
      email: pack(bags.email, ['email_subject', 'email_template_id', 'template_file_id', 'email_body']),
      whatsapp: pack(bags.whatsapp, ['whatsapp_body', 'whatsapp_template_id', 'whatsapp_template_name']),
    };
  }

  delayLabel(attempt: ChannelSetupAttempt, index = this.selectedAttemptIndex): string {
    if (attempt.timing === 'absolute') {
      return attempt.absolute_at ? 'Fixed date & time' : 'Pick a date & time';
    }
    return this.delayHumanMessage(attempt.delay_minutes, index);
  }

  delayHumanMessage(minutesValue: number | string | null | undefined, index = this.selectedAttemptIndex): string {
    const minutes = this.clampDelayMinutes(minutesValue, index);
    if (!minutes) {
      return 'Sends immediately.';
    }
    const days = Math.floor(minutes / MINUTES_PER_DAY);
    const hours = Math.floor((minutes % MINUTES_PER_DAY) / 60);
    const leftover = minutes % 60;
    const parts: string[] = [];
    if (days) {
      parts.push(days === 1 ? '1 day' : `${days} days`);
    }
    if (hours) {
      parts.push(hours === 1 ? '1 hour' : `${hours} hours`);
    }
    if (leftover) {
      parts.push(leftover === 1 ? '1 minute' : `${leftover} minutes`);
    }
    return `Sends after ${parts.join(' ')}.`;
  }

  attemptChannelChips(attempt: ChannelSetupAttempt): string[] {
    return this.attemptChannelLangChips(attempt).map((chip) => (
      chip.shorts.length
        ? `${chip.name} ${chip.shorts.map((item) => item.code).join(' ')}`
        : chip.name
    ));
  }

  attemptChannelsLabel(attempt: ChannelSetupAttempt): string {
    const labels = this.attemptChannelChips(attempt);
    return labels.length ? labels.join(' · ') : 'No channels';
  }

  attemptDisplayName(index: number): string {
    if (index <= 0) {
      return this.isOnDemand ? 'Backup Attempt' : 'Invitation';
    }
    return this.isOnDemand ? `Backup reminder ${index}` : `Reminder ${index}`;
  }

  attemptPad(index: number): string {
    return String(index + 1).padStart(2, '0');
  }

  tokenForMessageTab(field: { token: string; name: string }, channel: ChannelKey = this.activeTemplateChannel): string {
    if (channel === 'email') {
      return `{{${field.name}}}`;
    }
    return `--${field.name}--`;
  }

  rememberCaret(field: keyof ChannelSetupTemplate, event: Event): void {
    const el = event.target as HTMLTextAreaElement | HTMLInputElement;
    this.lastCaret = {
      field,
      start: el.selectionStart ?? 0,
      end: el.selectionEnd ?? el.selectionStart ?? 0,
    };
  }

  insertDynamicToken(field: { token: string; name: string }): void {
    const token = this.tokenForMessageTab(field);
    const targetField = this.resolveInsertField();
    const tpl = this.currentTemplate;
    const current = String(tpl[targetField] ?? '');
    const editor = this.editorFor(targetField);
    const live = Boolean(editor && document.activeElement === editor);
    const start = live
      ? (editor?.selectionStart ?? current.length)
      : (this.lastCaret?.field === targetField ? this.lastCaret.start : current.length);
    const end = live
      ? (editor?.selectionEnd ?? start)
      : (this.lastCaret?.field === targetField ? this.lastCaret.end : current.length);
    const safeStart = Math.max(0, Math.min(start, current.length));
    const safeEnd = Math.max(safeStart, Math.min(end, current.length));
    tpl[targetField] = `${current.slice(0, safeStart)}${token}${current.slice(safeEnd)}` as never;
    const caret = safeStart + token.length;
    this.lastCaret = { field: targetField, start: caret, end: caret };
    setTimeout(() => {
      const node = this.editorFor(targetField);
      if (!node) {
        return;
      }
      node.focus();
      node.setSelectionRange(caret, caret);
    });
  }

  isDynamicTokenUsed(field: { token: string; name: string }, channel: ChannelKey = this.activeTemplateChannel): boolean {
    const token = this.tokenForMessageTab(field, channel);
    const bag = this.selectedAttempt?.templates?.[channel] || {};
    return Object.values(bag).some((tpl) => this.channelTemplateText(channel, tpl).includes(token));
  }

  get usedMergeFields(): Array<{ token: string; name: string; label: string }> {
    return this.mergeFields.filter((item) => this.isDynamicTokenUsed(item));
  }

  get usesDynamicText(): boolean {
    return this.usedMergeFields.length > 0;
  }

  get dynamicTextHint(): string {
    const sample = this.activeTemplateChannel === 'email' ? '{{customerName}}' : '--customerName--';
    const channel = this.channelLabel(this.activeTemplateChannel);
    return `${channel} uses dynamic text. Placeholders like ${sample} are filled when we send.`;
  }

  private channelTemplateText(channel: ChannelKey, tpl: ChannelSetupTemplate): string {
    if (channel === 'sms') {
      return String(tpl.sms_body ?? '');
    }
    if (channel === 'whatsapp') {
      return String(tpl.whatsapp_body ?? '');
    }
    return `${tpl.email_subject ?? ''}\n${tpl.email_body ?? ''}`;
  }

  private resolveInsertField(): keyof ChannelSetupTemplate {
    if (this.activeTemplateChannel === 'sms') {
      return 'sms_body';
    }
    if (this.activeTemplateChannel === 'whatsapp') {
      return 'whatsapp_body';
    }
    if (this.lastCaret?.field === 'email_subject') {
      return 'email_subject';
    }
    return 'email_body';
  }

  private editorFor(field: keyof ChannelSetupTemplate): HTMLTextAreaElement | HTMLInputElement | null {
    if (field === 'sms_body') {
      return this.smsBodyInput?.nativeElement ?? null;
    }
    if (field === 'email_subject') {
      return this.emailSubjectInput?.nativeElement ?? null;
    }
    if (field === 'email_body') {
      return this.emailBodyInput?.nativeElement ?? null;
    }
    if (field === 'whatsapp_body') {
      return this.whatsappBodyInput?.nativeElement ?? null;
    }
    return null;
  }

  get previewSamples(): Record<string, string> {
    return {
      '--feedbackUrl--': this.sampleFeedbackUrl,
      '--unsubscribeUrl--': this.sampleUnsubscribeUrl,
    };
  }

  get customerConversationUrl(): string {
    return String(this.config.prerequisite?.customer_url || '').trim();
  }

  get sampleFeedbackUrl(): string {
    const base = this.customerConversationUrl;
    if (!base) {
      return `https://feedback.example.com/${SMS_UNIQUE_TOKEN_SAMPLE}`;
    }
    return `${base}${SMS_UNIQUE_TOKEN_SAMPLE}`;
  }

  get sampleUnsubscribeUrl(): string {
    const base = this.customerConversationUrl;
    if (!base) {
      return 'https://feedback.example.com/unsubscribe';
    }
    const trimmed = base.replace(/\/+$/, '');
    return `${trimmed}/unsubscribe`;
  }

  applyMergeTokens(value: string): string {
    let next = this.normalizeTemplateText(value);
    const samples = this.previewSamples;
    Object.keys(samples).forEach((token) => {
      const name = token.replace(/--/g, '');
      next = next.split(token).join(samples[token]);
      next = next.split(`{{${name}}}`).join(samples[token]);
    });
    return next;
  }

  formatTextPreview(value: string): SafeHtml {
    const merged = this.applyMergeTokens(value || '');
    const escaped = merged
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
    const withBreaks = escaped.replace(/\r\n|\n|\r/g, '<br>');
    const withLinks = withBreaks.replace(
      /(https?:\/\/[^\s<]+)/g,
      '<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>',
    );
    return this.sanitizer.bypassSecurityTrustHtml(withLinks);
  }

  get smsPreviewText(): string {
    return this.applyMergeTokens(this.currentTemplate.sms_body || '').trim();
  }

  get whatsappPreviewText(): string {
    return this.applyMergeTokens(this.currentTemplate.whatsapp_body || '').trim();
  }

  get emailPreviewSubject(): string {
    return this.applyMergeTokens(this.currentTemplate.email_subject || '').trim();
  }

  private normalizeTemplateText(value: string): string {
    return (value || '').replace(/\\n/g, '\n');
  }

  private decodeEmailHtml(html: string): string {
    const raw = html || '';
    if (/&lt;([a-z!\/])/i.test(raw) && !/<[a-z!\/]/i.test(raw)) {
      const textarea = document.createElement('textarea');
      textarea.innerHTML = raw;
      return textarea.value;
    }
    return raw;
  }

  private wrapEmailDocument(html: string): string {
    const body = html.trim();
    if (!body) {
      return '';
    }
    const viewport = '<meta name="viewport" content="width=device-width, initial-scale=1">';
    let next = body;
    if (/<html[\s>]/i.test(body) || /<!doctype/i.test(body)) {
      if (!/name=["']viewport["']/i.test(body)) {
        if (/<head[^>]*>/i.test(body)) {
          next = body.replace(/<head([^>]*)>/i, `<head$1>${viewport}`);
        } else {
          next = body.replace(/<html([^>]*)>/i, `<html$1><head>${viewport}</head>`);
        }
      }
    } else {
      next = `<!DOCTYPE html><html><head><meta charset="utf-8">${viewport}<base target="_blank"><style>html,body{margin:0;padding:12px;font-family:Arial,Helvetica,sans-serif;color:#111;background:#fff;}</style></head><body>${body}</body></html>`;
    }
    if (/http-equiv=["']Content-Security-Policy["']/i.test(next)) {
      return next;
    }
    if (/<head[^>]*>/i.test(next)) {
      return next.replace(/<head([^>]*)>/i, `<head$1>${EMAIL_PREVIEW_CSP}`);
    }
    return `${EMAIL_PREVIEW_CSP}\n${next}`;
  }

  private revokeEmailPreview(): void {
    if (this.emailPreviewObjectUrl) {
      URL.revokeObjectURL(this.emailPreviewObjectUrl);
      this.emailPreviewObjectUrl = '';
    }
    this.emailPreviewUrl = null;
  }

  private refreshEmailPreviewSrc(): void {
    this.revokeEmailPreview();
    if (!this.previewOpen || this.previewChannel !== 'email') {
      return;
    }
    const html = this.wrapEmailDocument(
      this.applyMergeTokens(this.decodeEmailHtml(this.currentTemplate.email_body || '')),
    );
    if (!html) {
      this.changeDetector.markForCheck();
      return;
    }
    void this.assignPreviewFrame(html);
  }

  private async assignPreviewFrame(html: string): Promise<void> {
    try {
      const url = await createHostedEmailPreviewUrl(html);
      this.emailPreviewUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
    } catch {
      this.emailPreviewObjectUrl = URL.createObjectURL(new Blob([html], { type: 'text/html' }));
      this.emailPreviewUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.emailPreviewObjectUrl);
    }
    this.changeDetector.markForCheck();
  }

  private async loadEmailHtmlWindow(html: string, win: Window): Promise<void> {
    const wrapped = this.wrapEmailDocument(html);
    if (!wrapped.trim()) {
      win.close();
      return;
    }
    try {
      const url = await createHostedEmailPreviewUrl(wrapped);
      win.location.replace(url);
    } catch {
      const url = URL.createObjectURL(new Blob([wrapped], { type: 'text/html' }));
      win.location.replace(url);
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    }
  }

  get emailFromLooksValid(): boolean {
    return EMAIL_IN_TEXT.test((this.config.channels.email.from || '').trim());
  }

  get replyToLooksValid(): boolean {
    const value = (this.config.channels.email.reply_to || '').trim();
    return !value || PLAIN_EMAIL.test(value);
  }

  get previewCustomerName(): string {
    return '';
  }

  get previewShopName(): string {
    return '';
  }

  get previewClock(): string {
    const now = new Date();
    return now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }

  get emailFromDisplayName(): string {
    const from = (this.config.channels.email.from || '').trim();
    if (!from) {
      return 'Your brand';
    }
    const named = from.match(/^(.+?)\s*<[^>]+>$/);
    return named ? named[1].trim() : from;
  }

  get previewFromInitials(): string {
    const name = this.emailFromDisplayName.replace(/[^A-Za-z0-9]+/g, ' ').trim();
    const parts = name.split(/\s+/).filter(Boolean);
    if (!parts.length) {
      return 'BR';
    }
    return parts.slice(0, 2).map((part) => part[0]).join('').toUpperCase();
  }

  get previewWhatsAppInitials(): string {
    const sender = (this.config.channels.whatsapp.sender_id || 'WA').trim();
    return sender.slice(0, 2).toUpperCase();
  }

  get smsRawBody(): string {
    return this.normalizeTemplateText(this.currentTemplate.sms_body || '');
  }

  get smsHasFeedbackUrlToken(): boolean {
    const body = this.smsRawBody;
    return body.includes('--feedbackUrl--') || body.includes('{{feedbackUrl}}');
  }

  get smsMissingUrlHint(): boolean {
    const body = this.smsRawBody;
    const hasFeedback = body.includes('--feedbackUrl--') || body.includes('{{feedbackUrl}}');
    const hasUnsubscribe = body.includes('--unsubscribeUrl--') || body.includes('{{unsubscribeUrl}}');
    return !hasFeedback || !hasUnsubscribe;
  }

  get smsFeedbackUrlCountedLength(): number {
    const base = this.customerConversationUrl;
    return (base ? base.length : 0) + SMS_UNIQUE_TOKEN_MAX;
  }

  get smsLinkHint(): string {
    const counted = this.smsFeedbackUrlCountedLength;
    if (!this.customerConversationUrl) {
      return `Feedback URL uses the project conversation URL plus a unique 10–12 character token. Counted as ${counted} characters until the conversation URL is set.`;
    }
    return `Feedback URL is the conversation URL plus a unique 10–12 character token. Counted as ${counted} characters.`;
  }

  get smsCountableText(): string {
    return this.applyMergeTokens(this.currentTemplate.sms_body || '');
  }

  private get smsMeasure(): { encoding: '7bit' | 'unicode'; charsUsed: number; parts: number; charsInSms: number } {
    return measureSms(this.smsCountableText, this.currentLanguage);
  }

  get smsEncoding(): '7bit' | 'unicode' {
    return this.smsMeasure.encoding;
  }

  get smsCountedLength(): number {
    return this.smsMeasure.charsUsed;
  }

  get smsCharLimit(): number {
    return this.smsMeasure.charsInSms;
  }

  get smsSegmentCount(): number {
    return this.smsMeasure.parts;
  }

  get smsOverGsmLimit(): boolean {
    const measure = this.smsMeasure;
    return measure.parts > 1;
  }

  get smsCountsAsEnglish(): boolean {
    return this.smsEncoding === '7bit';
  }

  onEmailTemplateSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0];
    input.value = '';
    if (!file || !this.brandId || !this.canUpdate) {
      return;
    }
    const isHtml = file.type === 'text/html' || /\.html?$/i.test(file.name);
    if (!isHtml) {
      this.showToast('ERROR', 'Invalid file', 'Upload an HTML email template (.html).', 15000);
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const html = String(reader.result || '');
      if (!html.trim()) {
        this.showToast('ERROR', 'Empty file', 'The HTML template is empty.', 15000);
        return;
      }
      this.prepareEmailHtml(html, file.name);
    };
    reader.onerror = () => {
      this.showToast('ERROR', 'Error', 'Could not read the HTML file.', 15000);
    };
    reader.readAsText(file);
  }

  private prepareEmailHtml(html: string, fileName: string): void {
    if (!this.canUpdate) {
      this.showToast('ERROR', 'Not authorized', 'Not authorized');
      return;
    }
    if (/<embed/i.test(html)) {
      this.showToast('ERROR', 'Invalid HTML', 'Please upload HTML without XSS tags such as embed.', 15000);
      return;
    }
    const missing = this.emailHtmlTokenWarning(html);
    if (missing) {
      this.openConfirm({
        title: 'Missing links in HTML',
        message: `${missing} Are you sure you want to upload?`,
        tone: 'ok',
        onConfirm: () => this.uploadEmailHtml(html, fileName),
      });
      return;
    }
    this.uploadEmailHtml(html, fileName);
  }

  private emailHtmlTokenWarning(html: string): string {
    const hasFeedback = this.htmlHasToken(html, 'feedbackUrl');
    const hasUnsubscribe = this.htmlHasToken(html, 'unsubscribeUrl');
    if (!hasFeedback && !hasUnsubscribe) {
      return 'Unique Feedback URL and Unsubscribe URL not available in HTML file.';
    }
    if (!hasUnsubscribe) {
      return 'Unsubscribe URL not available in HTML file.';
    }
    if (!hasFeedback) {
      return 'Unique Feedback URL not available in HTML file.';
    }
    return '';
  }

  private htmlHasToken(html: string, name: string): boolean {
    return html.includes(`{{${name}}}`) || html.includes(`--${name}--`);
  }

  private uploadEmailHtml(html: string, fileName: string): void {
    if (!this.brandId || !this.canUpdate) {
      return;
    }
    const nextHtml = html.slice(0, FIELD_MAX.emailHtml);
    this.uploadingEmail = true;
    this.emailHtmlLoadToken += 1;
    this.channelSetupService.uploadInviteEmailTemplate({
      html: nextHtml,
      file_name: fileName,
      brand_id: this.brandId,
      setup_mode: this.setupMode,
    }).subscribe({
      next: (uploaded) => {
        const tpl = this.currentTemplate;
        tpl.template_file_id = uploaded.template_file_id || '';
        tpl.email_template_id = '';
        tpl.template_file_name = uploaded.file_name || fileName;
        tpl.email_body = nextHtml;
        this.rememberEmailHtmlSnapshot(nextHtml);
        this.uploadingEmail = false;
        this.touchAttempt();
        if (this.previewOpen) {
          this.refreshEmailPreviewSrc();
        }
        this.showToast(
          'INFO',
          'HTML updated',
          'The email template is on this attempt. Save the attempt to keep it.',
          15000,
        );
      },
      error: (error) => {
        this.uploadingEmail = false;
        this.showToast(
          'ERROR',
          'Upload failed',
          error?.error?.message || error?.message || 'Could not upload the email template',
          15000,
        );
      },
    });
  }

  private emailTemplateForLanguage(lang = this.currentLanguage): ChannelSetupTemplate | undefined {
    return this.selectedAttempt?.templates?.email?.[lang];
  }

  private emailHtmlRequest(tpl: ChannelSetupTemplate | undefined) {
    const fileId = String(tpl?.template_file_id || '').trim();
    const catalogId = String(tpl?.email_template_id || '').trim();
    const brandId = this.brandId;
    const loadId = fileId || catalogId;
    if (!loadId || !brandId) {
      return null;
    }
    return this.channelSetupService.getInviteEmailTemplate(brandId, loadId);
  }

  private maybeLoadEmailPreview(): void {
    const index = this.selectedAttemptIndex;
    const lang = this.currentLanguage;
    const tpl = this.emailTemplateForLanguage(lang);
    const fileId = String(tpl?.template_file_id || '').trim();
    const catalogId = String(tpl?.email_template_id || '').trim();
    const loadId = fileId || catalogId;
    if (!tpl || !loadId || tpl.email_body?.trim()) {
      return;
    }
    const request$ = this.emailHtmlRequest(tpl);
    if (!request$) {
      return;
    }
    const loadKey = `${index}:${lang}:${loadId}`;
    if (this.loadingEmailHtml && this.emailHtmlLoadKey === loadKey) {
      return;
    }
    this.emailHtmlLoadKey = loadKey;
    this.loadingEmailHtml = true;
    const token = ++this.emailHtmlLoadToken;
    request$.subscribe({
      next: (template) => {
        if (token !== this.emailHtmlLoadToken || this.emailHtmlLoadKey !== loadKey) {
          return;
        }
        this.loadingEmailHtml = false;
        const live = this.config.attempts[index]?.templates?.email?.[lang];
        if (!live) {
          return;
        }
        const wasClean = !this.computeAttemptChanged(index) && !this.hasSharedChannelChanges;
        if (template?.html) {
          live.email_body = String(template.html);
          this.rememberEmailHtmlSnapshot(template.html, index, lang);
          if (this.previewOpen) {
            this.refreshEmailPreviewSrc();
          }
        }
        if (template?.file_name && !live.template_file_name) {
          live.template_file_name = template.file_name;
        }
        const loadedFileId = template?.gridfs_id || template?.template_file_id;
        if (loadedFileId && !live.template_file_id) {
          live.template_file_id = loadedFileId;
        }
        if (wasClean && this.isAttemptSaved(index) && this.config.attempts[index]) {
          this.persistedAttempts[index] = _.cloneDeep(this.config.attempts[index]);
          this.persistedAttemptJson[index] = this.attemptSnapshotJson(this.config.attempts[index]);
        }
        this.bumpDirtyCache();
        this.changeDetector.markForCheck();
      },
      error: () => {
        if (token !== this.emailHtmlLoadToken) {
          return;
        }
        this.loadingEmailHtml = false;
        this.changeDetector.markForCheck();
      },
    });
  }

  private buildSelectedAttemptSavePayload(): ChannelSetupConfig {
    const selected = this.selectedAttemptIndex;
    const liveCount = this.config.attempts.length;
    const persistCount = Math.min(this.persistedAttemptCount, liveCount);
    const attempts: ChannelSetupAttempt[] = [];

    for (let i = 0; i < persistCount; i++) {
      const live = this.config.attempts[i];
      if (i === selected) {
        attempts.push(_.cloneDeep(live));
      } else {
        attempts.push(_.cloneDeep(this.persistedAttempts[i] || live));
      }
    }
    if (selected >= persistCount) {
      for (let i = persistCount; i <= selected && i < liveCount; i++) {
        if (this.config.attempts[i]) {
          attempts.push(_.cloneDeep(this.config.attempts[i]));
        }
      }
    }

    return this.buildPayloadForAttempts(attempts);
  }

  private buildPayloadForAttempts(attemptsInput: ChannelSetupAttempt[]): ChannelSetupConfig {
    const attempts = attemptsInput.map((attempt, index) => {
      const next = _.cloneDeep(attempt);
      next.attempt = index + 1;
      next.timing = this.isOnDemand ? 'relative' : (attempt.timing === 'absolute' ? 'absolute' : 'relative');
      return next;
    });
    const payload = _.cloneDeep(this.config);
    payload.attempts = attempts;
    const used = new Set<string>();
    payload.attempts.forEach((attempt) => {
      (attempt.channels || []).forEach((channel) => used.add(channel));
    });
    payload.channels.sms.enabled = used.has('sms');
    payload.channels.email.enabled = used.has('email');
    payload.channels.whatsapp.enabled = used.has('whatsapp');
    const derived = this.derivedNotifyChannelFromAttempts(attempts);
    payload.notify_channel = String(this.config.notify_channel || '').toLowerCase() === 'none'
      ? 'none'
      : derived;
    return payload;
  }

  private derivedNotifyChannelFromAttempts(attempts: ChannelSetupAttempt[]): string {
    const used = new Set<string>();
    attempts.forEach((attempt) => {
      (attempt.channels || []).forEach((channel) => used.add(String(channel)));
    });
    const enabled = this.deliveryChannels.filter((key) => used.has(key));
    if (!enabled.length) {
      return 'none';
    }
    if (enabled.length > 1) {
      return 'all';
    }
    return enabled[0] === 'sms' ? 'phone' : enabled[0];
  }

  save(): void {
    if (!this.canSaveAttempt || !this.brandId) {
      return;
    }
    if (!this.canUpdate) {
      this.showToast(
        this.isOverAttemptLimit ? 'INFO' : 'ERROR',
        this.isOverAttemptLimit ? 'Cannot update' : 'Not authorized',
        this.isOverAttemptLimit ? this.attemptOverLimitMessage : 'Not authorized',
      );
      return;
    }
    if (!this.config.attempts.length) {
      this.showToast('ERROR', 'Cannot save', 'Get started and add at least one attempt first.', 15000);
      return;
    }
    const selectedAttempt = this.selectedAttempt;
    if (!selectedAttempt || !this.attemptHasAnyConfiguredChannel(selectedAttempt)) {
      this.showToast(
        'ERROR',
        'Choose a channel',
        'This attempt needs at least one delivery channel.',
        15000,
      );
      return;
    }
    const delayGap = this.delayMinutesGap(selectedAttempt);
    if (delayGap) {
      this.showToast('ERROR', 'Delay', delayGap, 15000);
      return;
    }
    if (this.sequenceWindowNotice.length) {
      const detail = `${this.sequenceWindowNotice.map((part) => part.text).join('')}Filters & timing.`;
      this.showToast('ERROR', 'Attempt timing', detail, 15000);
      return;
    }
    this.setDelayMinutes(selectedAttempt.delay_minutes);
    this.setAbsoluteAt(selectedAttempt.absolute_at || '');
    this.config.channels.sms.sender_id = String(this.config.channels.sms.sender_id || '').slice(0, FIELD_MAX.smsSender);
    this.config.channels.whatsapp.sender_id = String(this.config.channels.whatsapp.sender_id || '').slice(0, FIELD_MAX.whatsappSender);
    this.config.channels.email.from = String(this.config.channels.email.from || '').slice(0, FIELD_MAX.emailFrom);
    this.config.channels.email.reply_to = String(this.config.channels.email.reply_to || '').slice(0, FIELD_MAX.emailReply);
    const emailGap = this.emailSubjectGap(selectedAttempt);
    if (emailGap) {
      this.showToast('ERROR', 'Email', emailGap, 15000);
      return;
    }
    const smsGap = this.smsTemplateGap(selectedAttempt);
    if (smsGap) {
      this.showToast('ERROR', 'SMS template', smsGap, 15000);
      return;
    }
    const whatsappGap = this.whatsappTemplateGap(selectedAttempt);
    if (whatsappGap) {
      this.showToast('ERROR', 'WhatsApp template', whatsappGap, 15000);
      return;
    }
    if (selectedAttempt.channels.includes('sms') && this.config.channels.sms.sms_trim_enabled) {
      if (!this.smsTrimLengthValid || !this.smsTrimNameValid) {
        this.showToast('ERROR', 'SMS Trim', this.channelSetupGap('sms'), 15000);
        return;
      }
    }
    if (selectedAttempt?.channels.includes('email')) {
      if (!this.emailFromLooksValid) {
        this.showToast('ERROR', 'From address', this.channelSetupGap('email'), 15000);
        return;
      }
      if (!this.replyToLooksValid) {
        this.showToast('ERROR', 'Reply-to', this.channelSetupGap('email'), 15000);
        return;
      }
    }
    const selected = this.selectedAttemptIndex;
    const payload = this.buildSelectedAttemptSavePayload();
    this.saving = true;
    this.persistSetup(payload).subscribe({
      next: (saved) => {
        const keptAttempts = this.config.attempts.map((attempt) => _.cloneDeep(attempt));
        const normalized = this.normalizeConfig(saved);
        this.ignoreFormActivity = true;
        this.config = {
          ...normalized,
          attempts: keptAttempts.map((local, index) => (
            index === selected
              ? this.applySavedAttempt(normalized.attempts[index], local)
              : local
          )),
        };
        this.syncProjectChannelsFromAttempts();
        this.saving = false;
        this.syncMessageTab();
        this.markSelectedAttemptPersisted();
        this.clearSaveFormActivity();
        this.showToast('SUCCESS', 'Saved', `${this.attemptDisplayName(selected)} saved.`, 15000);
      },
      error: (error) => {
        this.saving = false;
        this.showToast(
          'ERROR',
          'Could not save',
          firstValidationMessage(error) || 'Failed to save notification setup',
          15000,
        );
      },
    });
  }

  private applySavedAttempt(saved: ChannelSetupAttempt | undefined, local: ChannelSetupAttempt): ChannelSetupAttempt {
    const next = _.cloneDeep(local);
    if (!next.templates) {
      next.templates = emptyChannelTemplates();
    }
    const savedEmail = saved?.templates?.email || {};
    Object.keys(savedEmail).forEach((lang) => {
      const fromServer = savedEmail[lang];
      const live = next.templates.email[lang] || emptyTemplate();
      next.templates.email[lang] = {
        ...live,
        email_template_id: fromServer?.email_template_id || '',
        template_file_id: fromServer?.template_file_id || live.template_file_id || '',
        template_file_name: live.template_file_name || fromServer?.template_file_name || '',
        email_body: live.email_body || '',
      };
    });
    return next;
  }

  private clearSaveFormActivity(): void {
    this.attemptTouched = false;
    queueMicrotask(() => {
      this.attemptTouched = false;
      this.ignoreFormActivity = false;
    });
  }

  private deriveNotifyChannel(): string {
    const enabled = this.enabledChannels;
    if (!enabled.length) {
      return 'all';
    }
    if (enabled.length > 1) {
      return 'all';
    }
    return enabled[0] === 'sms' ? 'phone' : enabled[0];
  }

  private syncProjectChannelsFromAttempts(): void {
    const used = new Set<string>();
    this.config.attempts.forEach((attempt, index) => {
      attempt.attempt = index + 1;
      attempt.timing = attempt.timing === 'absolute' ? 'absolute' : 'relative';
      (attempt.channels || []).forEach((channel) => used.add(channel));
    });
    this.config.channels.sms.enabled = used.has('sms');
    this.config.channels.email.enabled = used.has('email');
    this.config.channels.whatsapp.enabled = used.has('whatsapp');
  }

  private syncMessageTab(): void {
    const tabs = this.attemptMessageTabs;
    if (!tabs.includes(this.messageTab)) {
      this.messageTab = tabs[0] || 'sms';
    }
  }

  private normalizeConfig(raw: ChannelSetupConfig): ChannelSetupConfig {
    const base = emptyChannelSetup(raw?.brand_id || this.brandId || '');
    const merged: ChannelSetupConfig = {
      ...base,
      ...raw,
      prerequisite: { ...base.prerequisite, ...(raw?.prerequisite ?? {}) },
      channels: {
        email: { ...base.channels.email, ...(raw?.channels?.email ?? {}) },
        sms: { ...base.channels.sms, ...(raw?.channels?.sms ?? {}) },
        whatsapp: { ...base.channels.whatsapp, ...(raw?.channels?.whatsapp ?? {}) },
      },
      attempts: Array.isArray(raw?.attempts) && raw.attempts.length ? raw.attempts : base.attempts,
    };
    merged.notifications_enabled = raw?.notifications_enabled !== false;
    merged.notify_channel = String(raw?.notify_channel || merged.notify_channel || 'none').toLowerCase();
    merged.communication_channels = Array.isArray(raw?.communication_channels)
      ? raw.communication_channels.map((item) => String(item).toLowerCase())
      : (merged.communication_channels || []);
    merged.date_limit_interval = Number(raw?.date_limit_interval) > 0
      ? Math.round(Number(raw.date_limit_interval))
      : 5;
    merged.channels.sms.sms_trim_enabled = Boolean(merged.channels.sms.sms_trim_enabled);
    merged.channels.sms.recipient_name_length = this.clampSmsTrimLength(
      merged.channels.sms.recipient_name_length,
    );
    merged.channels.sms.default_recipient_name = merged.channels.sms.default_recipient_name || 'Customer';
    merged.prerequisite.customer_url = String(merged.prerequisite.customer_url || '').trim();
    merged.dlt_enabled = Boolean(raw?.dlt_enabled);
    merged.dlt_templates = Array.isArray(raw?.dlt_templates) ? raw.dlt_templates : [];
    merged.whatsapp_templates = Array.isArray(raw?.whatsapp_templates) ? raw.whatsapp_templates : [];
    delete merged.template_catalogs;
    merged.attempts = merged.attempts.map((attempt, index) => {
      const templates = this.normalizeAttemptTemplates(attempt.templates);
      const next: ChannelSetupAttempt = {
        ...attempt,
        attempt: attempt.attempt || index + 1,
        timing: this.isOnDemand ? 'relative' : (attempt.timing === 'absolute' ? 'absolute' : 'relative'),
        delay_minutes: this.clampDelayMinutes(attempt.delay_minutes, index),
        absolute_at: this.clampAbsoluteAt(attempt.absolute_at || '', index),
        based_on: index === 0 ? 1 : (Number(attempt.based_on) === 1 ? 1 : 2),
        channels: attempt.channels || [],
        templates,
      };
      next.channels = next.channels.filter((channel) => this.attemptHasChannelReady(next, channel as ChannelKey, merged.channels));
      next.default_languages = this.normalizeDefaultLanguages(next);
      return next;
    });
    return merged;
  }

  private normalizeAttemptTemplates(raw: ChannelSetupAttempt['templates'] | Record<string, ChannelSetupTemplate> | undefined): ChannelSetupAttempt['templates'] {
    const source = raw || {};
    const isSplit = Boolean((source as ChannelSetupAttempt['templates']).sms
      || (source as ChannelSetupAttempt['templates']).email
      || (source as ChannelSetupAttempt['templates']).whatsapp);
    if (isSplit) {
      const split = source as ChannelSetupAttempt['templates'];
      return {
        sms: this.normalizeLangBag(split.sms),
        email: this.normalizeLangBag(split.email),
        whatsapp: this.normalizeLangBag(split.whatsapp),
      };
    }
    const sms: Record<string, ChannelSetupTemplate> = {};
    const email: Record<string, ChannelSetupTemplate> = {};
    const whatsapp: Record<string, ChannelSetupTemplate> = {};
    Object.entries(source as Record<string, ChannelSetupTemplate>).forEach(([lang, tpl]) => {
      if (['sms', 'email', 'whatsapp'].includes(lang)) {
        return;
      }
      sms[lang] = { ...emptyTemplate(), sms_body: tpl?.sms_body || '' };
      email[lang] = { ...emptyTemplate(), ...tpl };
      whatsapp[lang] = {
        ...emptyTemplate(),
        whatsapp_body: tpl?.whatsapp_body || '',
        whatsapp_template_id: tpl?.whatsapp_template_id || '',
        whatsapp_template_name: tpl?.whatsapp_template_name || '',
      };
    });
    return {
      sms: this.normalizeLangBag(sms),
      email: this.normalizeLangBag(email),
      whatsapp: this.normalizeLangBag(whatsapp),
    };
  }

  private normalizeDefaultLanguages(attempt: ChannelSetupAttempt): ChannelSetupAttempt['default_languages'] {
    const pick = (channel: ChannelKey): string => {
      const keys = Object.keys(attempt.templates?.[channel] || {});
      const stored = attempt.default_languages?.[channel];
      if (stored && keys.includes(stored)) {
        return stored;
      }
      return keys[0] || '';
    };
    return {
      sms: pick('sms'),
      email: pick('email'),
      whatsapp: pick('whatsapp'),
    };
  }

  private normalizeLangBag(bag: Record<string, ChannelSetupTemplate> | undefined): Record<string, ChannelSetupTemplate> {
    const entries = Object.entries(bag || {});
    if (!entries.length) {
      return {};
    }
    return Object.fromEntries(entries.map(([lang, tpl]) => {
      const next = { ...emptyTemplate(), ...tpl };
      if (String(next.template_file_id || '').trim() || String(next.email_template_id || '').trim()) {
        next.email_body = '';
      }
      return [lang, next];
    }));
  }
}
